/* ============================================
   FOREIGN RESOURCE — Global JavaScript
   ============================================ */

(function() {
  'use strict';

  /* --- Scroll Reveal (Intersection Observer) --- */
  const initReveal = () => {
    const els = document.querySelectorAll('.reveal');
    if (!els.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
    els.forEach(el => observer.observe(el));
  };

  /* --- Mobile Menu --- */
  const initMobileMenu = () => {
    const toggle = document.querySelector('[data-menu-toggle]');
    const close = document.querySelector('[data-menu-close]');
    const overlay = document.querySelector('[data-menu-overlay]');
    if (!toggle || !overlay) return;

    const open = () => {
      overlay.classList.add('is-active');
      // The markup ships aria-hidden="true" and nothing used to change it, so the
      // menu stayed invisible to assistive tech even while open — while the line
      // below moved focus into that hidden subtree, which is an ARIA violation.
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      toggle.setAttribute('aria-expanded', 'true');
      // The first <a> in the overlay is the logo in the header row. Prefer a real
      // nav item and fall back only if the nav is empty.
      const firstLink = overlay.querySelector('.mobile-menu__nav a, .mobile-menu__nav button') ||
                        overlay.querySelector('a');
      if (firstLink) firstLink.focus();
    };

    const shut = () => {
      overlay.classList.remove('is-active');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.focus();
    };

    toggle.addEventListener('click', open);
    if (close) close.addEventListener('click', shut);
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) shut();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && overlay.classList.contains('is-active')) shut();
    });

    // Any menu item with children expands to reveal them. Each button's panel
    // is the element right after it in the markup.
    overlay.querySelectorAll('[data-mm-shop]').forEach((btn) => {
      const panel = btn.nextElementSibling;
      if (!panel || !panel.hasAttribute('data-mm-shop-panel')) return;
      btn.addEventListener('click', () => {
        const open = panel.hidden;
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  };

  /* --- Chrome: stack header below the announcement bar; expose offset --- */
  const initChrome = () => {
    const ann = document.querySelector('[data-announcement]');
    const header = document.querySelector('[data-header]');
    const setChrome = () => {
      const annH = ann ? ann.offsetHeight : 0;
      const headerH = header ? header.offsetHeight : 0;
      if (header) header.style.top = annH + 'px';
      document.documentElement.style.setProperty('--chrome-h', (annH + headerH) + 'px');
    };
    setChrome();
    window.addEventListener('resize', setChrome, { passive: true });
  };

  /* --- Announcement bar: cycle messages --- */
  const initAnnouncement = () => {
    const bar = document.querySelector('[data-announcement]');
    if (!bar) return;
    const msgs = bar.querySelectorAll('[data-ann-msg]');
    if (msgs.length < 2) return;
    const speed = (parseFloat(bar.dataset.rotate) || 4) * 1000;
    let i = 0;
    setInterval(() => {
      msgs[i].classList.remove('is-active');
      i = (i + 1) % msgs.length;
      msgs[i].classList.add('is-active');
    }, speed);
  };

  /* --- Header Scroll Behavior --- */
  const initStickyHeader = () => {
    const header = document.querySelector('[data-header]');
    if (!header || !header.dataset.sticky) return;

    // PDP immersive: on the product page, hide the header ~1s after load so the
    // large image fills the screen; reveal it again the moment the user scrolls.
    const isProduct = document.body.classList.contains('template-product');
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    let pdpHidden = false;
    if (isProduct && isMobile) {
      setTimeout(() => {
        if (window.scrollY < 20) { header.classList.add('is-hidden'); pdpHidden = true; }
      }, 1000);
    }

    let lastScroll = 0;
    const onScroll = () => {
      const current = window.scrollY;
      header.classList.toggle('is-scrolled', current > 50);
      if (pdpHidden) { header.classList.remove('is-hidden'); pdpHidden = false; lastScroll = current; return; }
      if (current > 300) {
        header.classList.toggle('is-hidden', current > lastScroll);
      } else {
        header.classList.remove('is-hidden');
      }
      lastScroll = current;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  /* --- Product Image Carousel / Swipe --- */
  const initCarousels = () => {
    document.querySelectorAll('[data-carousel]').forEach(carousel => {
      const track = carousel.querySelector('[data-carousel-track]');
      const prev = carousel.querySelector('[data-carousel-prev]');
      const next = carousel.querySelector('[data-carousel-next]');
      if (!track) return;

      const scrollAmount = () => track.firstElementChild ? track.firstElementChild.offsetWidth + parseInt(getComputedStyle(track).gap || 0) : 300;

      if (prev) prev.addEventListener('click', () => {
        track.scrollBy({ left: -scrollAmount(), behavior: 'smooth' });
      });
      if (next) next.addEventListener('click', () => {
        track.scrollBy({ left: scrollAmount(), behavior: 'smooth' });
      });
    });
  };

  /* --- Product Variant Selector (option toggle only) --- */
  const initVariantSelectors = () => {
    document.querySelectorAll('[data-variant-select]').forEach(container => {
      const buttons = container.querySelectorAll('[data-variant-option]');
      buttons.forEach(btn => {
        btn.addEventListener('click', () => {
          buttons.forEach(b => {
            b.classList.remove('is-selected');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('is-selected');
          btn.setAttribute('aria-pressed', 'true');
          container.dispatchEvent(new CustomEvent('variant:change', { bubbles: true }));
        });
      });
    });
  };

  /* --- PDP: resolve selected options to a real variant --- */
  const initPdpVariants = () => {
    const pdp = document.querySelector('[data-pdp]');
    if (!pdp) return;
    const dataEl = pdp.querySelector('[data-variant-json]');
    if (!dataEl) return;
    let variants = [];
    try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
    if (!variants.length) return;

    const groups = pdp.querySelectorAll('[data-variant-select][data-option-position]');
    const idInput = pdp.querySelector('[data-variant-id]');
    const addBtn = pdp.querySelector('[data-add-btn]');
    const addText = pdp.querySelector('.pdp__add-text');
    const mobileBtn = pdp.querySelector('[data-mobile-submit]');
    const mobileText = mobileBtn ? mobileBtn.querySelector('span') : null;
    const lowStockEl = pdp.querySelector('[data-low-stock]');
    const threshold = parseInt(pdp.dataset.lowStockThreshold, 10) || 5;

    let inventory = {};
    const invEl = pdp.querySelector('[data-inventory-json]');
    if (invEl) {
      try { inventory = JSON.parse(invEl.textContent); } catch (e) { inventory = {}; }
    }

    // Instalments keep their cents; prices drop a trailing .00
    const moneyExact = (cents) =>
      (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' });

    const money = (cents) =>
      (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).replace(/\.00$/, '');

    const selectedValues = () => {
      const vals = [];
      groups.forEach((g) => {
        const pos = parseInt(g.dataset.optionPosition, 10) - 1;
        const sel = g.querySelector('.is-selected') || g.querySelector('[aria-pressed="true"]');
        vals[pos] = sel ? sel.dataset.variantOption : null;
      });
      return vals;
    };

    // The colour is the only option that changes what you are looking at, so it
    // is the only one the gallery and the URL follow. Sizes share photographs.
    let lastColorway = null;
    const syncColorway = () => {
      const chip = pdp.querySelector('[data-variant-select] [data-colorway].is-selected');
      const cw = chip ? (chip.dataset.colorway || '') : '';
      if (cw === lastColorway) return;
      lastColorway = cw;
      if (window.FRPdpGallery) window.FRPdpGallery.showColorway(cw);

      // Saving is per colourway, so the hearts have to be re-pointed as the
      // colour changes — otherwise every colour of a product shares one save.
      // They sit outside the form and were previously left untouched entirely.
      const handle = pdp.dataset.productHandle;
      if (handle && window.FRWishlist) {
        const key = cw ? handle + '::' + cw : handle;
        pdp.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
          btn.dataset.wishlistToggle = key;
        });
        window.FRWishlist.updateButtons();
      }
    };

    // Deep links already work — Liquid reads ?variant= server-side — but nothing
    // ever wrote one, so a chosen colourway could not be shared or bookmarked.
    // replaceState rather than pushState: switching colours is refining one
    // decision, not somewhere to press Back to.
    let urlReady = false;
    const syncUrl = (variant) => {
      if (!urlReady) { urlReady = true; return; }
      if (!variant || !window.history || !history.replaceState) return;
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      } catch (e) { /* older browsers keep the plain URL */ }
    };

    const resolve = () => {
      const vals = selectedValues();
      const variant = variants.find((v) =>
        vals.every((val, i) => val == null || v['option' + (i + 1)] === val));

      // The colour is still never named on the PDP — the chip's is-selected rule
      // is the whole indication. What follows it now is the gallery.
      syncColorway();
      syncUrl(variant);

      const setState = (btn, textEl, priceSel) => {
        if (!btn) return;
        const priceEl = pdp.querySelector(priceSel);
        if (variant) {
          if (idInput) idInput.value = variant.id;
          if (priceEl) {
            // Rebuild the sale markup so a variant with a different compare-at
            // shows (or drops) the struck-through original.
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              priceEl.innerHTML =
                '<s class="price-was">' + money(variant.compare_at_price) + '</s>' +
                '<span class="price-now">' + money(variant.price) + '</span>';
            } else {
              priceEl.textContent = money(variant.price);
            }
          }
          if (variant.available) { btn.disabled = false; if (textEl) textEl.textContent = 'Add to cart'; }
          else { btn.disabled = true; if (textEl) textEl.textContent = 'Sold out'; }
        } else {
          btn.disabled = true;
          if (textEl) textEl.textContent = 'Unavailable';
        }
      };
      setState(addBtn, addText, '[data-price]');
      setState(mobileBtn, mobileText, '[data-mobile-price]');

      // Low stock — only for variants Shopify actually tracks
      if (lowStockEl) {
        const left = variant ? inventory[variant.id] : undefined;
        if (variant && variant.available && typeof left === 'number' && left > 0 && left <= threshold) {
          lowStockEl.textContent = left === 1 ? 'Last one left' : `Only ${left} left`;
          lowStockEl.hidden = false;
        } else {
          lowStockEl.hidden = true;
        }
      }

      // Sold out hands the row to Notify me. The buy button is hidden rather
      // than left sitting there disabled: a dead "Sold out" bar above a live
      // "Notify me" is two controls for one decision, and the shopper has to
      // work out which one is theirs. An unresolvable combination is NOT this
      // case — that stays "Unavailable", because there is no variant to be
      // notified about.
      //
      // Unless Klaviyo never loaded at all: initPdpNotify's watchdog sets
      // data-notify-dead, and then the disabled button is the only honest thing
      // left to show. Without this check a later variant change would hide it
      // again and leave the shopper with no control of any kind.
      const soldOut = !!variant && !variant.available;
      const notifyDead = pdp.dataset.notifyDead === 'true';
      pdp.querySelectorAll('[data-notify]').forEach((el) => {
        el.hidden = notifyDead || !soldOut;
      });
      if (addBtn) addBtn.hidden = soldOut && !notifyDead;
      if (mobileBtn) mobileBtn.hidden = soldOut && !notifyDead;

      // Pay-in-4 figure follows the variant price, and the line stands down on
      // variants priced outside the range Shop Pay will actually finance —
      // Liquid can only judge the variant the page loaded with.
      const inst = pdp.querySelector('[data-installment]');
      if (inst && variant) inst.textContent = moneyExact(variant.price / 4);

      const terms = pdp.querySelector('[data-terms]');
      if (terms && variant) {
        const min = Number(terms.dataset.termsMin) || 0;
        const max = Number(terms.dataset.termsMax) || Infinity;
        terms.hidden = variant.price < min || variant.price > max;
      }
    };

    groups.forEach((g) => g.addEventListener('variant:change', resolve));
    resolve();
  };

  /* --- Quantity Selector --- */
  const initQuantitySelectors = () => {
    document.querySelectorAll('[data-quantity]').forEach(container => {
      const input = container.querySelector('[data-quantity-input]');
      const minus = container.querySelector('[data-quantity-minus]');
      const plus = container.querySelector('[data-quantity-plus]');
      if (!input) return;

      const update = (val) => {
        const min = parseInt(input.min) || 1;
        const max = parseInt(input.max) || 99;
        input.value = Math.max(min, Math.min(max, val));
      };

      if (minus) minus.addEventListener('click', () => update(parseInt(input.value) - 1));
      if (plus) plus.addEventListener('click', () => update(parseInt(input.value) + 1));
      input.addEventListener('change', () => update(parseInt(input.value)));
    });
  };

  /* --- Accordion --- */
  const initAccordions = () => {
    document.querySelectorAll('[data-accordion]').forEach(accordion => {
      const items = accordion.querySelectorAll('[data-accordion-item]');
      items.forEach(item => {
        const trigger = item.querySelector('[data-accordion-trigger]');
        const content = item.querySelector('[data-accordion-content]');
        if (!trigger || !content) return;

        trigger.addEventListener('click', () => {
          const isOpen = item.classList.contains('is-open');

          // Close all others in same accordion
          items.forEach(other => {
            other.classList.remove('is-open');
            const otherContent = other.querySelector('[data-accordion-content]');
            const otherTrigger = other.querySelector('[data-accordion-trigger]');
            if (otherContent) otherContent.style.maxHeight = '0';
            if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
          });

          if (!isOpen) {
            item.classList.add('is-open');
            content.style.maxHeight = content.scrollHeight + 'px';
            trigger.setAttribute('aria-expanded', 'true');
          }
        });
      });
    });
  };

  /* --- Modal --- */
  const initModals = () => {
    document.querySelectorAll('[data-modal-open]').forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const modalId = trigger.dataset.modalOpen;
        const modal = document.getElementById(modalId);
        if (!modal) return;
        modal.classList.add('is-active');
        document.body.style.overflow = 'hidden';
        const closeBtns = modal.querySelectorAll('[data-modal-close]');
        closeBtns.forEach(btn => btn.addEventListener('click', () => {
          modal.classList.remove('is-active');
          document.body.style.overflow = '';
          trigger.focus();
        }));
        modal.addEventListener('click', (e) => {
          if (e.target === modal) {
            modal.classList.remove('is-active');
            document.body.style.overflow = '';
          }
        });
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        const active = document.querySelector('.modal.is-active');
        if (active) {
          active.classList.remove('is-active');
          document.body.style.overflow = '';
        }
      }
    });
  };

  /* --- Desktop Shop dropdown ---
     There was no JS here at all. The panel opened on CSS :hover, which on a touch
     screen means the browser's emulated hover: the first tap opens instead of
     following the link, and nothing closes it but tapping elsewhere. aria-expanded
     was hardcoded "false" in the markup and never moved, so it lied permanently.

     The CSS hover path still exists for mice, now behind
     @media (hover: hover) and (pointer: fine). This drives .is-open for everyone
     else and keeps the attribute honest. */
  const initShopMenu = () => {
    document.querySelectorAll('[data-shop-menu]').forEach(initDropdown);
  };

  // One per top-level menu item that has children. Opening one closes the
  // others: the document click handler below sees a click outside each.
  const initDropdown = (shop) => {
    const trigger = shop.querySelector('.header__shop-trigger');
    const panel = shop.querySelector('.header__dropdown');
    if (!trigger || !panel) return;

    const setOpen = (on) => {
      shop.classList.toggle('is-open', on);
      trigger.setAttribute('aria-expanded', on ? 'true' : 'false');
    };

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      setOpen(!shop.classList.contains('is-open'));
    });

    document.addEventListener('click', (e) => {
      if (!shop.contains(e.target)) setOpen(false);
    });

    // A link inside is a navigation, so close on the way out rather than leaving
    // the panel hanging open behind the next page.
    panel.addEventListener('click', (e) => {
      if (e.target.closest('a')) setOpen(false);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && shop.classList.contains('is-open')) {
        setOpen(false);
        trigger.focus();
      }
    });

    shop.addEventListener('focusout', () => {
      requestAnimationFrame(() => {
        if (!shop.contains(document.activeElement)) setOpen(false);
      });
    });
  };

  /* --- Cart Drawer --- */
  const initCartDrawer = () => {
    const drawer = document.querySelector('[data-cart-drawer]');
    if (!drawer) return;
    const content = drawer.querySelector('#cart-drawer-content');

    // Same output as Liquid's money filter, from the shop's own format, so the
    // drawer reads "$174" where the cart page and PDP do. Falls back to the
    // browser's currency formatting if layout/theme.liquid has not synced yet.
    const moneyFormat = String((window.FRTheme && window.FRTheme.moneyFormat) || '').replace(/<[^>]*>/g, '');
    const money = (cents) => {
      if (!moneyFormat) {
        return (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' });
      }
      const n = cents / 100;
      const fmt = (d, t, sep) => {
        const parts = n.toFixed(d).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, t);
        return parts.join(sep);
      };
      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
        if (key === 'amount_no_decimals') return fmt(0, ',', '.');
        if (key === 'amount_with_comma_separator') return fmt(2, '.', ',');
        if (key === 'amount_no_decimals_with_comma_separator') return fmt(0, '.', ',');
        if (key === 'amount_with_apostrophe_separator') return fmt(2, "'", '.');
        return fmt(2, ',', '.');
      });
    };

    // Titles come from /cart.js as raw strings and go straight into innerHTML.
    const esc = (s) =>
      String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    // The drawer never waits on the network to show something. The cart the page
    // was rendered with (FRTheme.cart) draws it before anyone opens it; opening
    // refreshes it in the background. A quantity change redraws at once from the
    // known unit price, and /cart/change.js requests queue behind each other so
    // fast taps land in order. Only the answer to the LAST request in a burst is
    // drawn, so an early reply cannot undo a later tap.
    let current = (window.FRTheme && window.FRTheme.cart && Array.isArray(window.FRTheme.cart.items)) ? window.FRTheme.cart : null;
    let queue = Promise.resolve();
    let pending = 0;
    let lastPct = 0;
    const imgSrc = (image) => {
      const url = image && typeof image === 'object' ? image.src || image.url : image;
      return url ? String(url).replace(/(\.[^./?]+)(\?|$)/, '_120x$1$2') : '';
    };

    // Size is changeable in the drawer. /cart.js says which variant a line is but
    // not what else the product comes in, so each product's variants are fetched
    // once (on open, not on every page view) and held here by handle. Until they
    // arrive the line shows its size as text, as it always did. Every option but
    // colour gets a select — the same rule the PDP uses for its size buttons.
    // Colour stays text: a colourway is its own card and its own photographs,
    // and swapping it from a 70px thumbnail is not where that choice is made.
    // Seeded from the page (layout/theme.liquid renders the cart's products), so
    // the first draw already has its selects and opening never swaps them in.
    const products = Object.assign({}, (window.FRTheme && window.FRTheme.cartProducts) || {});
    const loading = {};
    const isColour = (name) => /^colou?r$/i.test(String(name || '').trim());
    const productRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const loadProducts = async (cart) => {
      if (!cart || !cart.items) return false;
      const handles = cart.items
        .filter((it) => !it.product_has_only_default_variant && it.handle && !products[it.handle] && !loading[it.handle])
        .map((it) => it.handle)
        .filter((h, i, all) => all.indexOf(h) === i);
      if (!handles.length) return false;
      await Promise.all(handles.map(async (h) => {
        loading[h] = true;
        try {
          const res = await fetch(productRoot + 'products/' + encodeURIComponent(h) + '.js');
          if (res.ok) products[h] = await res.json();
        } catch (e) {}
        delete loading[h];
      }));
      return true;
    };
    // A sold-out answer to a size change, shown under that line until the next change.
    let lineNote = null;
    // Which size panel is open ("lineKey:optionIndex"), so a background redraw
    // while it is open draws it open again instead of snapping it shut.
    let openMenu = null;

    const optionsMarkup = (item) => {
      const product = products[item.handle];
      const chosen = (item.options_with_values || []).map((o) => o.value);
      if (!product || !Array.isArray(product.options) || !Array.isArray(product.variants) ||
          product.variants.length < 2 || !chosen.length) {
        return item.variant_title ? '<p class="cart-drawer__item-variant">' + esc(item.variant_title) + '</p>' : '';
      }
      const parts = product.options.map((opt, i) => {
        const name = opt && typeof opt === 'object' ? opt.name : opt;
        const values = (opt && opt.values) || [];
        const value = chosen[i];
        if (value == null) return '';
        if (isColour(name) || values.length < 2) {
          return '<span class="cart-drawer__opt-value">' + esc(value) + '</span>';
        }
        // Each value is offered as the variant it would actually become: this
        // line's other options, with this one changed. Sold-out sizes stay in the
        // list, disabled, so the run of sizes reads whole.
        //
        // The choice opens as a small panel built like the collection filter's —
        // salt ground, hairline border, a micro-caps heading, tick-box rows — not
        // the browser's own select menu, which no stylesheet reaches and which
        // the OS places wherever it likes.
        const menuId = item.key + ':' + i;
        const isOpen = openMenu === menuId;
        const choices = values.map((v) => {
          const target = chosen.slice();
          target[i] = v;
          const variant = product.variants.find((pv) =>
            (pv.options || []).every((o, k) => o === target[k]));
          if (!variant) return '';
          const isCurrent = v === value;
          const soldOut = !variant.available && !isCurrent;
          return '<button type="button" role="menuitemradio" class="cart-drawer__opt-choice' + (soldOut ? ' is-disabled' : '') + '"' +
              ' aria-checked="' + isCurrent + '" data-cart-variant="' + variant.id + '"' + (soldOut ? ' disabled' : '') + '>' +
            '<span class="cart-drawer__opt-box" aria-hidden="true"></span>' +
            '<span>' + esc(v) + '</span>' +
            (soldOut ? '<span class="cart-drawer__opt-count">Sold out</span>' : '') +
          '</button>';
        }).join('');
        return '<div class="cart-drawer__opt">' +
          '<button type="button" class="cart-drawer__opt-toggle" data-cart-opt-toggle="' + esc(menuId) + '"' +
              ' aria-haspopup="true" aria-expanded="' + isOpen + '" aria-label="' + esc(name) + ' ' + esc(value) + ', change ' + esc(name).toLowerCase() + '">' +
            '<span class="cart-drawer__opt-name">' + esc(name) + '</span>' +
            '<span class="cart-drawer__opt-current">' + esc(value) + '</span>' +
          '</button>' +
          '<div class="cart-drawer__opt-menu' + (isOpen ? ' is-open' : '') + '" role="menu" aria-label="' + esc(name) + '">' +
            '<p class="cart-drawer__opt-heading">' + esc(name) + '</p>' +
            choices +
          '</div>' +
        '</div>';
      }).filter(Boolean);
      return '<div class="cart-drawer__item-variant cart-drawer__item-options">' + parts.join('') + '</div>';
    };

    const draw = (cart) => {
      if (!content || !cart) return;
      current = cart;
      // keep header counts in sync
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
        el.textContent = cart.item_count;
        el.classList.toggle('is-active', cart.item_count > 0);
      });
      if (!cart.item_count) {
        content.innerHTML = '<p class="cart-drawer__empty">Your cart is empty</p>';
        return;
      }
      // The threshold is resolved PER COUNTRY in layout/theme.liquid, off the same
      // generated rates table the PDP estimator reads. It is 0 in the 132 countries
      // that have no free-shipping tier, and 0 means no bar — not a bar at zero.
      //
      // That distinction is the whole point of this block. The bar used to read one
      // global setting, so a shopper in Tokyo was told they were "$40 away from
      // complimentary shipping", nudged to add an item to cross it, and then charged
      // $25 at checkout. Manufacturing an upsell against a benefit that does not
      // exist in that country is a deceptive shipping claim, not a rounding error.
      //
      // The figure arrives in SHOP currency because that is what the rates table is
      // written in, while cart.total_price is in PRESENTMENT currency. On a EUR
      // storefront those differ by the FX rate, so convert before comparing —
      // otherwise the bar is wrong by ~13% and money() dresses the error up in the
      // right symbol. Shopify.currency is emitted by content_for_header; if it is
      // missing the rate is 1, which is correct for a single-currency store.
      const fx = parseFloat(
        (window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1
      ) || 1;
      const thresholdShop = (window.FRTheme && window.FRTheme.freeShippingThreshold) || 0;
      const threshold = Math.round(thresholdShop * fx);
      let shipNote = '';
      if (threshold > 0) {
        const remaining = threshold - cart.total_price;
        const earned = remaining <= 0;
        const pct = Math.max(0, Math.min(100, (cart.total_price / threshold) * 100));
        // The fill renders at zero width and is widened on the next frame, because
        // draw() replaces this whole element — a width set in the same paint as the
        // insertion has nothing to transition from and would just snap.
        shipNote =
          '<div class="cart-drawer__ship' + (earned ? ' is-earned' : '') + '">' +
            '<p class="cart-drawer__shipnote">' +
              (earned
                ? 'Complimentary shipping is on us'
                : 'You’re ' + money(remaining) + ' away from complimentary shipping') +
            '</p>' +
            '<div class="cart-drawer__ship-track" role="progressbar" aria-valuemin="0" ' +
              'aria-valuemax="100" aria-valuenow="' + Math.round(pct) + '" ' +
              'aria-label="Progress toward complimentary shipping">' +
              '<span class="cart-drawer__ship-fill" style="width:' + lastPct.toFixed(2) + '%" data-ship-fill="' + pct.toFixed(2) + '"></span>' +
            '</div>' +
          '</div>';
      }
      // Lines are addressed by item.key, not by position. A removal renumbers every
      // line below it, so an index captured at render time can delete the wrong row.
      content.innerHTML = shipNote + cart.items.map((item) =>
        '<div class="cart-drawer__item" data-cart-item="' + esc(item.key) + '">' +
          '<a class="cart-drawer__item-media" href="' + esc(item.url) + '" tabindex="-1" aria-hidden="true">' +
            '<img src="' + esc(imgSrc(item.image)) + '" alt="" width="60" height="75">' +
          '</a>' +
          '<div class="cart-drawer__item-info">' +
            '<div class="cart-drawer__item-head">' +
              '<a class="cart-drawer__item-title" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
              // Remove is a glyph at the row's corner rather than a labelled link
              // under it: one line of chrome per item instead of two, and the
              // aria-label carries the meaning the × cannot.
              '<button type="button" class="cart-drawer__remove" data-cart-remove aria-label="Remove ' + esc(item.product_title) + ' from cart">&#215;</button>' +
            '</div>' +
            optionsMarkup(item) +
            (lineNote && lineNote.key === item.key ? '<p class="cart-drawer__item-note" role="status">' + esc(lineNote.text) + '</p>' : '') +
            '<div class="cart-drawer__item-foot">' +
              '<div class="cart-drawer__qty">' +
                '<button type="button" class="cart-drawer__step" data-cart-step="-1" aria-label="Decrease quantity for ' + esc(item.product_title) + '">&#8722;</button>' +
                '<input type="number" class="cart-drawer__qty-input" value="' + item.quantity + '" min="0" inputmode="numeric" data-cart-qty aria-label="Quantity for ' + esc(item.product_title) + '">' +
                '<button type="button" class="cart-drawer__step" data-cart-step="1" aria-label="Increase quantity for ' + esc(item.product_title) + '">+</button>' +
              '</div>' +
              '<p class="cart-drawer__item-price">' + money(item.final_line_price) + '</p>' +
            '</div>' +
          '</div>' +
        '</div>').join('') +
        '<div class="cart-drawer__footer"><div class="cart-drawer__subtotal"><span>Subtotal</span><span>' + money(cart.total_price) + '</span></div>' +
        '<p class="cart-drawer__note">Taxes and shipping calculated at checkout</p>' +
        '<form action="/cart" method="post"><button type="submit" name="checkout" class="btn btn--primary btn--full">Checkout</button></form>' +
        '<a href="/cart" class="cart-drawer__view-cart">View cart</a></div>';

      const fill = content.querySelector('[data-ship-fill]');
      if (fill) {
        lastPct = parseFloat(fill.getAttribute('data-ship-fill')) || 0;
        requestAnimationFrame(() => {
          fill.style.width = fill.getAttribute('data-ship-fill') + '%';
        });
      }
    };

    const fetchCart = async () => (await fetch('/cart.js', { cache: 'no-store' })).json();
    const sameCart = (a, b) => !!a && !!b && a.total_price === b.total_price &&
      a.items.length === b.items.length &&
      a.items.every((it, i) => it.key === b.items[i].key && it.quantity === b.items[i].quantity);

    // Background refresh: redraws only if the cart moved since it was last drawn
    // (another tab, an add from elsewhere), so an unchanged drawer never blinks.
    const render = async () => {
      if (!content) return;
      try {
        const cart = await fetchCart();
        if (!pending && !sameCart(cart, current)) draw(cart);
      } catch (e) {}
    };

    // Optimistic: apply the change to the cart already held and draw it now.
    // Quantity 0 is how the Cart API removes a line, and the row goes at once.
    const predict = (key, quantity) => {
      if (!current) return;
      const items = [];
      current.items.forEach((it) => {
        if (it.key !== key) { items.push(it); return; }
        if (quantity <= 0) return;
        items.push(Object.assign({}, it, { quantity: quantity, final_line_price: it.final_price * quantity }));
      });
      draw(Object.assign({}, current, {
        items: items,
        item_count: items.reduce((n, it) => n + it.quantity, 0),
        total_price: items.reduce((n, it) => n + it.final_line_price, 0),
      }));
    };

    // The Cart API has no "change variant", so a size change is an add of the new
    // size followed by a removal of the old line — in that order, so a size that
    // sold out a moment ago fails the add and leaves the line exactly as it was.
    // Properties and a selling plan travel with it. Adding a size that is already
    // in the cart merges into that line, which is what a shopper would expect.
    //
    // Like a quantity tap, it is drawn at once and confirmed behind: the line
    // shows the new size and price the moment it is picked, with nothing greyed
    // and nothing waiting. A failure redraws the truth with a note under the line.
    //
    // The new line's key is only known once Shopify answers, so taps made in the
    // meantime still carry the old one. `aliases` maps old key to new, and every
    // queued request resolves its key when it runs, not when it was tapped.
    const aliases = {};
    const resolveKey = (key) => { let k = key; while (aliases[k]) k = aliases[k]; return k; };
    const sameProps = (a, b) => JSON.stringify(a.properties || {}) === JSON.stringify(b.properties || {});

    const swapLine = (key, variantId) => {
      if (!key || !variantId || !current) return;
      const item = current.items.find((it) => it.key === key);
      if (!item || item.variant_id === variantId) return;
      lineNote = null;

      const product = products[item.handle];
      const variant = product && product.variants.find((v) => v.id === variantId);
      const unit = variant && typeof variant.price === 'number' ? variant.price : item.final_price;
      const into = current.items.find((it) => it !== item && it.variant_id === variantId && sameProps(it, item));
      const items = [];
      current.items.forEach((it) => {
        if (it === item) {
          if (into) return;
          const names = (item.options_with_values || []).map((o) => o.name);
          const opts = (variant && variant.options) || [];
          items.push(Object.assign({}, it, {
            variant_id: variantId,
            variant_title: opts.length ? opts.join(' / ') : it.variant_title,
            options_with_values: opts.length ? opts.map((v, i) => ({ name: names[i], value: v })) : it.options_with_values,
            final_price: unit,
            final_line_price: unit * it.quantity,
          }));
          return;
        }
        if (it === into) {
          const q = it.quantity + item.quantity;
          items.push(Object.assign({}, it, { quantity: q, final_line_price: it.final_price * q }));
          return;
        }
        items.push(it);
      });
      draw(Object.assign({}, current, {
        items: items,
        item_count: items.reduce((n, it) => n + it.quantity, 0),
        total_price: items.reduce((n, it) => n + it.final_line_price, 0),
      }));

      const line = { id: variantId, quantity: item.quantity };
      if (item.properties && Object.keys(item.properties).length) line.properties = item.properties;
      const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan;
      if (plan && plan.id) line.selling_plan = plan.id;

      pending++;
      queue = queue.then(async () => {
        let cart = null;
        let failed = false;
        const oldKey = resolveKey(key);
        try {
          const add = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [line] }),
          });
          if (add.ok) {
            const added = await add.json();
            const newKey = added && added.items && added.items[0] && added.items[0].key;
            const res = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ id: oldKey, quantity: 0 }),
            });
            if (newKey && newKey !== oldKey) aliases[oldKey] = newKey;
            cart = res.ok ? await res.json() : await fetchCart();
          } else {
            failed = true;
            let msg = '';
            try { msg = (await add.json()).description || ''; } catch (e) {}
            lineNote = { key: oldKey, text: msg || 'That size is no longer available.' };
            cart = await fetchCart();
          }
        } catch (e) {
          failed = true;
          try { cart = await fetchCart(); } catch (err) {}
        }
        pending--;
        // A failure is drawn even mid-burst, so the size snaps back at once.
        if (cart && (!pending || failed)) draw(cart);
      });
    };

    const changeLine = (key, quantity) => {
      if (!key) return;
      lineNote = null;
      quantity = Math.max(0, quantity);
      predict(key, quantity);
      pending++;
      queue = queue.then(async () => {
        let cart = null;
        try {
          const res = await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: resolveKey(key), quantity: quantity }),
          });
          // A 422 means the line is gone or the quantity is unavailable; the cart
          // refetched is the truth either way.
          cart = res.ok ? await res.json() : await fetchCart();
        } catch (e) {
          try { cart = await fetchCart(); } catch (err) {}
        }
        pending--;
        if (!pending && cart) draw(cart);
      });
    };

    // Delegated: draw() replaces the whole of #cart-drawer-content on every change,
    // so listeners bound to individual rows would not survive the first click.
    if (content) {
      const keyOf = (el) => {
        const row = el.closest('[data-cart-item]');
        return row ? row.getAttribute('data-cart-item') : null;
      };
      const setMenu = (id) => {
        openMenu = id;
        content.querySelectorAll('[data-cart-opt-toggle]').forEach((t) => {
          const on = t.getAttribute('data-cart-opt-toggle') === id;
          t.setAttribute('aria-expanded', String(on));
          const menu = t.parentNode.querySelector('.cart-drawer__opt-menu');
          if (menu) menu.classList.toggle('is-open', on);
        });
      };
      content.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-cart-opt-toggle]');
        if (toggle) {
          const id = toggle.getAttribute('data-cart-opt-toggle');
          setMenu(openMenu === id ? null : id);
          return;
        }
        const choice = e.target.closest('[data-cart-variant]');
        if (choice) {
          openMenu = null;
          if (choice.getAttribute('aria-checked') === 'true') { setMenu(null); return; }
          swapLine(keyOf(choice), parseInt(choice.getAttribute('data-cart-variant'), 10));
          return;
        }
        if (openMenu && !e.target.closest('.cart-drawer__opt-menu')) setMenu(null);
        const step = e.target.closest('[data-cart-step]');
        if (step) {
          const row = step.closest('[data-cart-item]');
          const input = row && row.querySelector('[data-cart-qty]');
          if (!input) return;
          const next = (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-cart-step'), 10);
          changeLine(keyOf(step), next);
          return;
        }
        const remove = e.target.closest('[data-cart-remove]');
        if (remove) changeLine(keyOf(remove), 0);
      });
      content.addEventListener('change', (e) => {
        const input = e.target.closest('[data-cart-qty]');
        if (input) changeLine(keyOf(input), parseInt(input.value, 10) || 0);
      });
      // Escape shuts an open size panel first, and only then the drawer.
      content.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && openMenu) {
          e.stopPropagation();
          const id = openMenu;
          setMenu(null);
          const t = content.querySelector('[data-cart-opt-toggle="' + id.replace(/"/g, '\\"') + '"]');
          if (t) t.focus();
        }
      });
      // A tap anywhere else in the drawer closes the panel.
      drawer.addEventListener('click', (e) => {
        if (openMenu && !e.target.closest('.cart-drawer__opt')) setMenu(null);
      });
      // Enter in the number field submits nothing; commit the value instead.
      content.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && e.target.closest('[data-cart-qty]')) {
          e.preventDefault();
          e.target.blur();
        }
      });
    }

    const show = () => {
      drawer.classList.add('is-active');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };
    // The icon opens on what is already drawn and refreshes behind it.
    // Anything the page did not seed (a line added in another tab, or a page
    // served while layout/theme.liquid lagged behind this file) is fetched while
    // the drawer is still closed, and redrawn out of sight.
    const loadOptions = async () => {
      if (await loadProducts(current) && !pending && current) draw(current);
    };
    const open = () => {
      if (current) draw(current);
      show();
      render().then(loadOptions);
    };
    // After an AJAX add the held cart is known to be stale, so fetch first and
    // open on the new cart rather than flashing the old one. Its product's sizes
    // are fetched before the drawer shows too (usually already held: the PDP
    // preloads its own below), so the new line arrives with its select.
    const openFresh = async () => {
      try {
        const cart = await fetchCart();
        await loadProducts(cart);
        draw(cart);
      } catch (e) {}
      show();
    };
    const close = () => {
      drawer.classList.remove('is-active');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    document.querySelectorAll('[data-cart-toggle]').forEach((btn) =>
      btn.addEventListener('click', (e) => {
        // "Dedicated page" cart type: follow the link to /cart instead
        if (window.FRTheme && window.FRTheme.cartType === 'page') return;
        e.preventDefault();
        open();
      }));
    drawer.querySelectorAll('[data-cart-drawer-close]').forEach((b) => b.addEventListener('click', close));
    const overlay = drawer.querySelector('.cart-drawer__overlay');
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-active')) close();
    });

    // Draw now, so the first open shows the cart rather than an empty panel.
    if (current) draw(current);
    loadOptions();
    // On a product page, hold that product's sizes before Add to cart is pressed.
    const pdp = document.querySelector('[data-add-to-cart]') && location.pathname.match(/\/products\/([^/?#]+)/);
    if (pdp) loadProducts({ items: [{ handle: decodeURIComponent(pdp[1]) }] });

    // Let other code open the drawer after an AJAX add
    window.FROpenCart = openFresh;
  };

  /* --- Product card image slider ---
     Each card pages through that product's shots. Touch swiping is the
     browser's own scroll-snap, so this only paints the progress fill (shown on
     desktop hover only) and adds the desktop hover-scrub. */
  const initProductCardGalleries = () => {
    const tracks = document.querySelectorAll('[data-card-track]');
    if (!tracks.length) return;

    // Hover-scrub is a mouse idea. On touch the swipe IS the interaction, and
    // a synthesised hover would fight it.
    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    tracks.forEach((track) => {
      const slides = track.children;
      const total = slides.length;
      if (total < 2) return;

      const wrapper = track.closest('.product-card__image-wrapper');
      const fill = wrapper && wrapper.querySelector('[data-card-progress]');
      const setFill = (frac) => {
        if (!fill) return;
        fill.style.transform = 'scaleX(' + Math.min(1, Math.max(0, frac)) + ')';
      };

      // Position 1 of n at rest, not an empty bar.
      setFill(1 / total);

      track.addEventListener('scroll', () => {
        setFill((track.scrollLeft + track.clientWidth) / track.scrollWidth);
      }, { passive: true });

      if (!wrapper) return;

      // Desktop: pointer position across the card selects the image, which is
      // what makes a grid browsable without clicking into anything.
      let shown = 0;
      wrapper.addEventListener('pointermove', (e) => {
        if (!canHover.matches) return;
        const r = wrapper.getBoundingClientRect();
        if (!r.width) return;
        const idx = Math.min(total - 1, Math.max(0, Math.floor(((e.clientX - r.left) / r.width) * total)));
        if (idx === shown) return;
        shown = idx;
        // Instant, not smooth: a smooth scroll queues up behind every sample
        // and the image lags the cursor by a slide or more.
        track.scrollTo({ left: track.clientWidth * idx, behavior: 'auto' });
      });

      wrapper.addEventListener('pointerleave', () => {
        if (!canHover.matches || shown === 0) return;
        shown = 0;
        track.scrollTo({ left: 0, behavior: 'auto' });
      });
    });

    // The whole card is a link, so a swipe would land as a click and navigate.
    // Same 10px threshold initPdpZoom uses to tell a drag from a tap.
    document.querySelectorAll('.product-card__link').forEach((link) => {
      let sx = 0, sy = 0, dragged = false;
      link.addEventListener('pointerdown', (e) => {
        sx = e.clientX; sy = e.clientY; dragged = false;
      });
      link.addEventListener('pointermove', (e) => {
        if (Math.abs(e.clientX - sx) > 10 || Math.abs(e.clientY - sy) > 10) dragged = true;
      });
      link.addEventListener('click', (e) => {
        if (dragged) { e.preventDefault(); dragged = false; }
      });
    });
  };

  /* --- Collection Filters --- */
  const initFilters = () => {
    const toggles = document.querySelectorAll('[data-filter-toggle]');
    if (!toggles.length) return;

    // Closing a menu always resets its button, so aria-expanded can't say
    // "open" about a panel that an outside tap already shut.
    const close = (menu) => {
      menu.classList.remove('is-open');
      const wrap = menu.closest('[data-sort-wrap], [data-filters-wrap]');
      const btn = wrap && wrap.querySelector('[data-filter-toggle]');
      if (btn) btn.setAttribute('aria-expanded', 'false');
    };
    const openMenus = () => document.querySelectorAll('.jc-collection__menu.is-open');

    toggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        const target = document.querySelector(toggle.dataset.filterToggle);
        if (!target) return;
        const willOpen = !target.classList.contains('is-open');
        openMenus().forEach(m => { if (m !== target) close(m); });
        target.classList.toggle('is-open', willOpen);
        toggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
      });
    });

    // Close on outside click
    document.addEventListener('click', (e) => {
      openMenus().forEach(menu => {
        if (!menu.closest('[data-sort-wrap], [data-filters-wrap]').contains(e.target)) close(menu);
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') openMenus().forEach(close);
    });

    // The filter panel is a plain GET form and works without this. All this
    // adds: an empty price box is left out of the URL instead of arriving as
    // "price.gte=", and a min above the max is swapped rather than returning
    // an empty grid.
    document.querySelectorAll('[data-filter-form]').forEach((form) => {
      form.addEventListener('submit', () => {
        const prices = form.querySelectorAll('.jc-price__input');
        if (prices.length === 2) {
          const [lo, hi] = prices;
          if (lo.value !== '' && hi.value !== '' && Number(lo.value) > Number(hi.value)) {
            [lo.value, hi.value] = [hi.value, lo.value];
          }
        }
        prices.forEach((input) => { if (input.value.trim() === '') input.disabled = true; });
      });
    });

    // Back/forward cache restores the page with the boxes still disabled.
    window.addEventListener('pageshow', () => {
      document.querySelectorAll('[data-filter-form] .jc-price__input:disabled').forEach((i) => { i.disabled = false; });
    });
  };

  /* --- Card tags: salt over dark photography, slate over pale ---
     SOLD OUT / NEW sit straight on the photo with no plate behind them, so a
     fixed slate disappears into a dark skin tone or a black garment. Measure
     the pixels actually under the tag and pick whichever of salt or slate has
     the higher contrast against them.

     The measurement reads a 96px copy of the slide, never the displayed image:
     putting crossOrigin on a visible <img> refetches it and, if the CDN ever
     stopped sending CORS headers, would blank the photo. The worst a failed
     probe can do here is leave the tag slate, which is what it was before.
     Re-measured after a swipe, because the tag stays put while the photo
     under it changes. */
  const initBadgeContrast = () => {
    const badges = document.querySelectorAll('.product-card__badge:not([data-contrast-bound])');
    if (!badges.length) return;

    const SALT = [241, 238, 230];   // --color-secondary
    const SLATE = [45, 44, 47];     // .product-card__badge default
    const lin = (c) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    const lum = (rgb) => 0.2126 * lin(rgb[0]) + 0.7152 * lin(rgb[1]) + 0.0722 * lin(rgb[2]);
    const ratio = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
    const saltL = lum(SALT), slateL = lum(SLATE);

    const probes = new Map();
    const probe = (src) => {
      if (!probes.has(src)) {
        probes.set(src, new Promise((resolve) => {
          const p = new Image();
          p.crossOrigin = 'anonymous';
          p.decoding = 'async';
          p.onload = () => resolve(p);
          p.onerror = () => resolve(null);
          p.src = /[?&]width=\d+/.test(src)
            ? src.replace(/([?&]width=)\d+/, (m, k) => k + '96')
            : src + (src.includes('?') ? '&' : '?') + 'width=96';
        }));
      }
      return probes.get(src);
    };

    const measure = (badge) => {
      const wrapper = badge.closest('.product-card__image-wrapper');
      const track = wrapper && wrapper.querySelector('[data-card-track]');
      if (!track) return;
      const idx = track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
      const slide = track.children[idx] || track.children[0];
      const img = slide && slide.querySelector('img');
      const src = img && img.getAttribute('src');
      if (!src) return;

      const w = wrapper.getBoundingClientRect();
      const b = badge.getBoundingClientRect();
      if (!w.width || !w.height || !b.width) return;

      probe(src).then((p) => {
        if (!p || !p.naturalWidth) return;
        try {
          // Map the tag's box (plus a few px of breathing room) from the card
          // onto the probe, through the same object-fit: cover crop the
          // displayed photo uses.
          const pad = 4;
          const nw = p.naturalWidth, nh = p.naturalHeight;
          const scale = Math.max(w.width / nw, w.height / nh);
          const ox = (nw * scale - w.width) / 2;
          const oy = (nh * scale - w.height) / 2;
          const x0 = Math.max(0, (b.left - w.left - pad + ox) / scale);
          const y0 = Math.max(0, (b.top - w.top - pad + oy) / scale);
          const x1 = Math.min(nw, (b.right - w.left + pad + ox) / scale);
          const y1 = Math.min(nh, (b.bottom - w.top + pad + oy) / scale);
          if (x1 - x0 < 1 || y1 - y0 < 1) return;

          const c = document.createElement('canvas');
          c.width = 16; c.height = 8;
          const ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(p, x0, y0, x1 - x0, y1 - y0, 0, 0, 16, 8);
          const d = ctx.getImageData(0, 0, 16, 8).data;
          let bg = 0;
          for (let i = 0; i < d.length; i += 4) bg += lum([d[i], d[i + 1], d[i + 2]]);
          bg /= d.length / 4;
          badge.classList.toggle('is-on-dark', ratio(saltL, bg) > ratio(slateL, bg));
        } catch (e) {
          // Tainted canvas: keep slate
        }
      });
    };

    const io = 'IntersectionObserver' in window
      ? new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (!en.isIntersecting) return;
            io.unobserve(en.target);
            measure(en.target);
          });
        }, { rootMargin: '300px 0px' })
      : null;

    badges.forEach((badge) => {
      badge.setAttribute('data-contrast-bound', '');
      if (io) io.observe(badge); else measure(badge);

      const track = badge.closest('.product-card__image-wrapper')?.querySelector('[data-card-track]');
      if (!track || track.children.length < 2) return;
      let t;
      track.addEventListener('scroll', () => {
        clearTimeout(t);
        t = setTimeout(() => measure(badge), 120);
      }, { passive: true });
    });

    // The crop under the tag shifts when the card changes shape. Bound once:
    // this runs again whenever recommendations arrive.
    if (initBadgeContrast.resizeBound) return;
    initBadgeContrast.resizeBound = true;
    let rt;
    window.addEventListener('resize', () => {
      clearTimeout(rt);
      rt = setTimeout(() => document.querySelectorAll('.product-card__badge[data-contrast-bound]').forEach(measure), 200);
    }, { passive: true });
  };

  /* --- Add to Cart (AJAX) --- */
  const initAddToCart = () => {
    document.querySelectorAll('[data-add-to-cart]').forEach(form => {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        const btn = form.querySelector('[data-add-btn]');
        if (!btn || btn.disabled) return;

        // Feedback on both the desktop button and the mobile sticky band.
        // Update only the text labels so icon/price markup is preserved.
        const mobileBtn = document.querySelector('[data-mobile-submit]');
        const labels = [btn.querySelector('.pdp__add-text') || btn];
        if (mobileBtn) labels.push(mobileBtn.querySelector('span') || mobileBtn);
        const originals = labels.map((el) => el.textContent);
        const setLabel = (txt) => labels.forEach((el) => { el.textContent = txt; });
        const setDisabled = (state) => {
          btn.disabled = state;
          if (mobileBtn) mobileBtn.disabled = state;
        };

        setDisabled(true);
        setLabel('Adding...');

        try {
          const formData = new FormData(form);
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            body: formData
          });

          if (res.ok) {
            setLabel('Added');
            if (window.FRTheme && window.FRTheme.cartType === 'page') {
              window.location.href = '/cart';
              return;
            }
            if (window.FROpenCart) {
              // Re-renders drawer contents and cart count badge, then opens it
              window.FROpenCart();
            } else {
              const cart = await (await fetch('/cart.js', { cache: 'no-store' })).json();
              document.querySelectorAll('[data-cart-count]').forEach((el) => {
                el.textContent = cart.item_count;
                el.classList.toggle('is-active', cart.item_count > 0);
              });
            }
          } else {
            const data = await res.json();
            setLabel(data.description || 'Error');
          }
        } catch (err) {
          setLabel('Error');
        }

        setTimeout(() => {
          labels.forEach((el, i) => { el.textContent = originals[i]; });
          setDisabled(false);
        }, 2000);
      });
    });
  };

  /* --- Wishlist (localStorage) --- */
  const Wishlist = {
    key: 'fr_wishlist',
    get() {
      try { return JSON.parse(localStorage.getItem(this.key)) || []; }
      catch { return []; }
    },
    save(items) {
      localStorage.setItem(this.key, JSON.stringify(items));
      this.updateCounts();
      this.updateButtons();
    },
    toggle(handle) {
      const items = this.get();
      const idx = items.indexOf(handle);
      if (idx > -1) { items.splice(idx, 1); }
      else { items.push(handle); }
      this.save(items);
      return idx === -1; // true if added
    },
    has(handle) {
      return this.get().includes(handle);
    },

    // A saved entry is "handle" for a whole product, or "handle::colour" for a
    // single colourway. Saves written before colourways existed are the first
    // shape and remain valid keys, so there is nothing to migrate.
    parse(key) {
      const str = String(key);
      const at = str.indexOf('::');
      if (at === -1) return { key: str, handle: str, colorway: '' };
      return { key: str, handle: str.slice(0, at), colorway: str.slice(at + 2) };
    },

    // Shopify's handleize, near enough: the colour in a key was handleized by
    // Liquid, so anything compared against it has to be handleized the same way.
    handleize(value) {
      return String(value).toLowerCase().trim()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    },

    // Resolve a saved entry against a /products/x.js payload. Lives here rather
    // than in a renderer because three of them need it — the card, the drawer
    // and the saved-items page — and they had already drifted into three
    // different ideas of how to show a product.
    view(product, colorway) {
      const out = {
        image: product.featured_image || (product.images && product.images[0]) || '',
        label: '',
        url: '/products/' + product.handle
      };
      if (!colorway) return out;

      const variant = (product.variants || []).find((v) =>
        (v.options || []).some((o) => this.handleize(o) === colorway));
      if (!variant) return out;

      out.label = (variant.options || []).find((o) => this.handleize(o) === colorway) || '';
      out.url += '?variant=' + variant.id;
      if (variant.featured_image && variant.featured_image.src) {
        out.image = variant.featured_image.src;
      }
      return out;
    },
    updateCounts() {
      const count = this.get().length;
      document.querySelectorAll('[data-wishlist-count]').forEach(el => {
        el.textContent = count;
        el.style.display = count > 0 ? '' : 'none';
      });
    },
    updateButtons() {
      document.querySelectorAll('[data-wishlist-toggle]').forEach(btn => {
        const handle = btn.dataset.wishlistToggle;
        const isSaved = this.has(handle);
        btn.classList.toggle('is-saved', isSaved);
        btn.setAttribute('aria-pressed', isSaved ? 'true' : 'false');
        const svg = btn.querySelector('svg');
        if (svg) svg.setAttribute('fill', isSaved ? 'currentColor' : 'none');
      });
    }
  };

  const initWishlist = () => {
    Wishlist.updateCounts();
    Wishlist.updateButtons();

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-wishlist-toggle]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const handle = btn.dataset.wishlistToggle;
      const added = Wishlist.toggle(handle);
      // Brief animation feedback
      btn.style.transform = 'scale(1.2)';
      setTimeout(() => { btn.style.transform = ''; }, 200);
    });
  };

  /* --- Wishlist Drawer (opens from the heart icon) --- */
  const initWishlistDrawer = () => {
    const drawer = document.querySelector('[data-wishlist-drawer]');
    if (!drawer) return;
    const grid = drawer.querySelector('[data-wishlist-grid]');
    const empty = drawer.querySelector('[data-wishlist-empty]');

    // Instalments keep their cents; prices drop a trailing .00
    const moneyExact = (cents) =>
      (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' });

    const money = (cents) =>
      (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' })
        .replace(/\.00$/, '');

    const render = async () => {
      const items = window.FRWishlist ? window.FRWishlist.get() : [];
      if (!items.length) {
        empty.style.display = '';
        grid.innerHTML = '';
        return;
      }
      empty.style.display = 'none';
      // Two colourways of one product are two entries but one fetch.
      const entries = items.map((k) => window.FRWishlist.parse(k));
      const handles = [...new Set(entries.map((e) => e.handle))];
      const byHandle = {};
      await Promise.all(handles.map(async (h) => {
        try { const r = await fetch('/products/' + h + '.js'); if (r.ok) byHandle[h] = await r.json(); }
        catch (e) {}
      }));

      const valid = entries.filter((e) => byHandle[e.handle]);
      if (!valid.length) { empty.style.display = ''; grid.innerHTML = ''; return; }
      grid.innerHTML = valid.map((entry) => {
        const p = byHandle[entry.handle];
        const v = window.FRWishlist.view(p, entry.colorway);
        return '<div class="wl-item">' +
          '<button class="wl-item__remove" data-wishlist-remove="' + entry.key + '" aria-label="Remove">' +
            '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
          '</button>' +
          '<a href="' + v.url + '" class="wl-item__link">' +
            '<div class="wl-item__img">' + (v.image ? '<img src="' + v.image + '" alt="" loading="lazy">' : '') + '</div>' +
            '<div class="wl-item__info">' +
              '<span class="wl-item__title">' + p.title + '</span>' +
              (v.label ? '<span class="wl-item__colorway">' + v.label + '</span>' : '') +
              '<span class="wl-item__price">' + money(p.price) + '</span>' +
            '</div>' +
          '</a>' +
        '</div>';
      }).join('');
    };

    const open = () => {
      render();
      drawer.classList.add('is-active');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };
    const close = () => {
      drawer.classList.remove('is-active');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    document.querySelectorAll('[data-wishlist-open]').forEach((b) =>
      b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    drawer.querySelectorAll('[data-wishlist-close]').forEach((b) =>
      b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-active')) close();
    });

    grid.addEventListener('click', (e) => {
      const rm = e.target.closest('[data-wishlist-remove]');
      if (!rm) return;
      e.preventDefault();
      if (window.FRWishlist) window.FRWishlist.toggle(rm.dataset.wishlistRemove);
      render();
    });
  };

  // Expose globally for wishlist page
  window.FRWishlist = Wishlist;

  /* --- Search Drawer (swipe up) with predictive results + most viewed --- */
  const initSearchDrawer = () => {
    const drawer = document.querySelector('[data-search-drawer]');
    if (!drawer) return;
    const input = drawer.querySelector('[data-search-input]');
    const results = drawer.querySelector('[data-search-results]');
    const dflt = drawer.querySelector('[data-search-default]');
    const mv = drawer.querySelector('[data-search-mostviewed]');

    // Instalments keep their cents; prices drop a trailing .00
    const moneyExact = (cents) =>
      (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' });

    const money = (cents) =>
      (cents / 100).toLocaleString(undefined, { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).replace(/\.00$/, '');

    const priceHTML = (p) => {
      const price = parseFloat(p.price);
      if (isNaN(price)) return '';
      const cmpRaw = p.compare_at_price_max || p.compare_at_price_min || p.compare_at_price;
      const cmp = cmpRaw != null ? parseFloat(cmpRaw) : NaN;
      if (!isNaN(cmp) && cmp > price) {
        return '<s class="sd-item__price-compare">' + money(Math.round(cmp * 100)) + '</s> ' +
               '<span class="sd-item__price-sale">' + money(Math.round(price * 100)) + '</span>';
      }
      return money(Math.round(price * 100));
    };

    const itemHTML = (url, image, title, priceStr) =>
      '<a href="' + url + '" class="sd-item">' +
        '<div class="sd-item__img">' + (image ? '<img src="' + image + '" alt="" loading="lazy">' : '') + '</div>' +
        '<div class="sd-item__title">' + title + '</div>' +
        '<div class="sd-item__price">' + (priceStr || '') + '</div>' +
      '</a>';

    // Most viewed = the shopper's recently viewed items (per-user, on device)
    const renderMostViewed = async () => {
      if (!mv) return;
      let items = [];
      try { items = JSON.parse(localStorage.getItem('fr_recently_viewed')) || []; } catch (e) {}
      const label = mv.previousElementSibling;
      if (!items.length) { if (label) label.style.display = 'none'; return; }
      const products = await Promise.all(items.slice(0, 4).map(async (h) => {
        try { const r = await fetch('/products/' + h + '.js'); if (r.ok) return r.json(); } catch (e) {}
        return null;
      }));
      const valid = products.filter(Boolean);
      if (!valid.length) { if (label) label.style.display = 'none'; return; }
      if (label) label.style.display = '';
      mv.innerHTML = valid.map((p) => itemHTML('/products/' + p.handle, p.featured_image || (p.images && p.images[0]), p.title, money(p.price))).join('');
    };

    const open = () => {
      renderMostViewed();
      drawer.classList.add('is-active');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(() => input && input.focus(), 250);
    };
    const close = () => {
      drawer.classList.remove('is-active');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };

    document.querySelectorAll('[data-search-open]').forEach((b) =>
      b.addEventListener('click', (e) => {
        e.preventDefault();
        const mm = document.querySelector('[data-menu-overlay]');
        if (mm) mm.classList.remove('is-active');
        open();
      }));
    drawer.querySelectorAll('[data-search-close]').forEach((b) => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-active')) close();
    });

    // Predictive search
    let t;
    if (input) input.addEventListener('input', () => {
      clearTimeout(t);
      const q = input.value.trim();
      if (q.length < 2) { results.hidden = true; results.innerHTML = ''; if (dflt) dflt.hidden = false; return; }
      t = setTimeout(async () => {
        try {
          const r = await fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=8');
          if (!r.ok) return;
          const data = await r.json();
          const products = (data.resources && data.resources.results && data.resources.results.products) || [];
          if (dflt) dflt.hidden = true;
          results.hidden = false;
          results.innerHTML = products.length
            ? products.map((p) => itemHTML(p.url, p.image || p.featured_image, p.title, priceHTML(p))).join('')
            : '<p class="search-drawer__label" style="grid-column:1/-1">No results — press enter to search.</p>';
        } catch (e) {}
      }, 250);
    });
  };

  /* --- PDP Tabs --- */
  const initPdpTabs = () => {
    document.querySelectorAll('[data-pdp-tabs]').forEach(container => {
      const tabs = container.querySelectorAll('[data-tab]');
      const panels = container.querySelectorAll('[data-tab-panel]');
      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          // Clicking the open tab closes it, so the panels stay hidden by default.
          const wasOpen = tab.classList.contains('is-active');
          tabs.forEach(t => {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
            t.setAttribute('aria-expanded', 'false');
          });
          panels.forEach(p => p.classList.remove('is-active'));
          if (wasOpen) return;
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');
          tab.setAttribute('aria-expanded', 'true');
          const panel = container.querySelector(`[data-tab-panel="${tab.dataset.tab}"]`);
          if (panel) panel.classList.add('is-active');
        });
      });
    });
  };

  /* --- PDP detail rows (Fabric / Colour / Packaging dropdowns) ---
     The accordion only. Company Details turns itself into a static column band
     above 769px, and owns that behaviour in its own section file — nothing
     about it belongs in the shared bundle. */
  const initPdpRows = () => {
    document.querySelectorAll('[data-pdp-rows]').forEach(group => {
      group.querySelectorAll('[data-row-trigger]').forEach(trigger => {
        const panel = trigger.parentElement.querySelector('[data-row-panel]');
        if (!panel) return;
        trigger.addEventListener('click', () => {
          const open = trigger.getAttribute('aria-expanded') === 'true';
          // One row open at a time keeps the column from running away
          group.querySelectorAll('[data-row-trigger]').forEach(t => t.setAttribute('aria-expanded', 'false'));
          group.querySelectorAll('[data-row-panel]').forEach(p => { p.hidden = true; });
          if (!open) {
            trigger.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
          }
        });
      });
    });
  };

  /* --- PDP Content panels (Engineering / Fabric / Colour / Packaging) --- */
  const initContentPanels = () => {
    document.querySelectorAll('[data-panel-media]').forEach(panel => {
      const slides = panel.querySelectorAll('[data-panel-slide]');
      const thumbs = panel.querySelectorAll('[data-panel-thumb]');
      if (slides.length < 2) return;

      thumbs.forEach(thumb => {
        thumb.addEventListener('click', () => {
          const idx = thumb.dataset.panelThumb;
          slides.forEach(s => s.classList.toggle('is-active', s.dataset.panelSlide === idx));
          thumbs.forEach(t => t.classList.toggle('is-active', t === thumb));
        });
      });
    });
  };

  /* --- PDP Delivery estimator + rolling urgency --- */
  // Renamed with the switch from postcodes to countries; the old key is left
  // behind deliberately so a stored zip cannot be read back as a country code.
  /* --- Clear what earlier builds persisted ---
     Two of them saved the tab's country choice, and one saved it from a picker
     that was later taken off the buy line, leaving a country no visible
     control could change. Nothing reads either key now; this sweeps them up so
     they are not left sitting in a shopper's browser forever. Stamped, so it
     runs once per browser rather than writing on every load. */
  const DEST_RESET_KEY = 'fr_dest_reset';
  const DEST_RESET_VERSION = '3';
  try {
    if (localStorage.getItem(DEST_RESET_KEY) !== DEST_RESET_VERSION) {
      localStorage.removeItem('fr_delivery_country');
      localStorage.removeItem('fr_market_country');
      localStorage.setItem(DEST_RESET_KEY, DEST_RESET_VERSION);
    }
  } catch (e) {
    // Private mode or blocked storage: nothing saved, so nothing to sweep.
  }

  /* --- Which country the Shipping and Returns tabs open on ---
     The footer's Shipping Country is the default, and moving it moves both
     tabs. Choosing a country inside either tab overrides that, and the override
     survives navigation until the shopper changes it again — or until they move
     the footer picker, which is the more deliberate statement of the two and
     therefore wins.

     Telling those apart needs memory: the market country is stamped on every
     load, so if it has moved since we last looked, the footer is what moved it
     and the stale override is dropped. Without that stamp a saved country
     outranks the footer forever, which is the bug this fixes. */
  /* --- Where the product-info pickers open, every single load ---
     The shopper's own market country, and nothing else.

     This used to persist a choice made inside the Shipping or Returns tab and
     reapply it on later loads, which is what let a shopper read "United
     Kingdom" in the tab while the footer and the prices said United States --
     on a different page, days later, with no memory of having chosen it.

     Those tabs are now standalone: a country picked in one moves the other
     and the line under the buy button, because they are one panel of facts
     about one product, and then it is forgotten. Nothing it does reaches the
     footer, the market, the currency or the next page load. The market is set
     by Shopify from the visitor's IP and changed only by the footer, which is
     the one control that moves prices with it. */
  const resolveCountry = (market) => (market || '').toUpperCase();

  // Adds whole business days, skipping weekends.
  const addBusinessDays = (from, days) => {
    const d = new Date(from.getTime());
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  };

  // The cutoff belongs to the warehouse, not the visitor. A shopper in New York
  // at 3pm is at noon in Los Angeles and still inside a 1pm cutoff — reading the
  // browser clock would tell them they missed a window they still have an hour of.
  // The returned Date carries the store's wall clock in its local fields, so
  // getDay/getHours and the business-day arithmetic below all agree on one zone.
  const zonedNow = (tz) => {
    const now = new Date();
    if (!tz) return now;
    try {
      const shifted = new Date(now.toLocaleString('en-US', { timeZone: tz }));
      return isNaN(shifted.getTime()) ? now : shifted;
    } catch (e) {
      // An unrecognised zone must not take the estimator — or the PDP — down.
      return now;
    }
  };

  // The countdown's own target, and deliberately NOT the dispatch day.
  //
  // dispatchStart below rolls over weekends, because the warehouse does. That
  // is right for the arrival date and wrong for a countdown: it makes the
  // clock read "2d 8h" from Friday afternoon to Monday, which is not a
  // countdown and carries no urgency at all.
  //
  // So the clock runs to the same wall-clock cutoff every calendar day and
  // resets at it, Saturday and Sunday included. Be clear-eyed about the cost:
  // on a weekend, beating this clock earns nothing, because the parcel leaves
  // on Monday either way and the date beside it says so. The date stays honest
  // -- it is still derived from the real dispatch day -- but the deadline is a
  // deadline only on a weekday. Shipped at the owner's explicit direction.
  const dailyCutoff = (cutoffHour, tz) => {
    const now = zonedNow(tz);
    const target = new Date(now.getTime());
    target.setHours(cutoffHour, 0, 0, 0);
    // Past today's, the clock restarts against tomorrow's rather than stalling
    // at zero or going negative.
    if (target.getTime() <= now.getTime()) target.setDate(target.getDate() + 1);
    return target;
  };

  // Orders placed after the cutoff, or at a weekend, dispatch the next business day.
  const dispatchStart = (cutoffHour, tz) => {
    const now = zonedNow(tz);
    let start = new Date(now.getTime());
    const day = start.getDay();
    if (now.getHours() >= cutoffHour || day === 0 || day === 6) {
      start = addBusinessDays(start, 1);
    }
    return start;
  };

  // Whole minutes until the cutoff of the day the order actually dispatches —
  // today's before the cutoff, the next business day's after it. Past 1pm the
  // clock restarts against tomorrow rather than dying at "order today", and
  // because the arrival date is derived from the same dispatch day, beating
  // this countdown is exactly what earns the date shown beside it.
  // Both instants sit in the same shifted frame, so the difference is real
  // elapsed time. Rounding up keeps the tail honest: 30s out reads "1m".
  const minsToCutoff = (now, start, cutoffHour) => {
    const target = new Date(start.getTime());
    target.setHours(cutoffHour, 0, 0, 0);
    return Math.ceil((target.getTime() - now.getTime()) / 60000);
  };

  // Deriving every part from one total is what stops the old "1h 60m" on the
  // hour. Days appear because a Friday-afternoon order counts down to Monday's
  // cutoff, and "71h" is not a number anyone parses at a glance.
  const fmtCountdown = (totalMins) => {
    const d = Math.floor(totalMins / 1440);
    const h = Math.floor((totalMins % 1440) / 60);
    const m = totalMins % 60;
    if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`;
    if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`;
    return `${m}m`;
  };

  // "Wed, Oct 14th".
  //
  // This replaced relative phrasing -- "tomorrow", "Wednesday", "next Tuesday".
  // That read more naturally and told the shopper less: a weekday on its own
  // has to be counted forward against a calendar before it can be checked, and
  // "next Tuesday" is ambiguous in a way a date never is. The weekday is what
  // someone plans around and the date is what they verify it against, so the
  // line carries both.
  //
  // The ordinal is English, like the sentence it sits inside. The weekday and
  // month follow the page's locale, as every other date in this file does.
  const fmtArrival = (d) => {
    const day = d.getDate();
    // 11th, 12th and 13th are the three the mod-10 rule gets wrong.
    const teen = day % 100 >= 11 && day % 100 <= 13;
    const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th');
    return d.toLocaleDateString(undefined, { weekday: 'short' })
      + ', ' + d.toLocaleDateString(undefined, { month: 'short' })
      + ' ' + day + suffix;
  };

  // Assembled as nodes, never innerHTML — the zip is read back from
  // localStorage, which is shopper-supplied and tamperable.
  const setLine = (el, parts) => {
    el.textContent = '';
    parts.forEach((part) => {
      const str = part[0];
      if (!str) return;
      if (part[1]) {
        const strong = document.createElement('strong');
        strong.textContent = str;
        el.appendChild(strong);
      } else {
        el.appendChild(document.createTextNode(str));
      }
    });
  };

  // Arrival windows drop the weekday — two of them side by side with the tier
  // label is already a lot of characters for a 390px viewport.
  const fmtShort = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  const fmtWindow = (from, lo, hi) => {
    const a = addBusinessDays(from, lo);
    const b = addBusinessDays(from, hi);
    // A one-day window would otherwise read "Aug 5 – Aug 5".
    return a.getTime() === b.getTime() ? fmtShort(a) : fmtShort(a) + ' – ' + fmtShort(b);
  };

  // Liquid writes the country tables through `| escape`, which is what stops a
  // price string closing the <script> that carries it. But <script> is raw text:
  // the browser never decodes character references inside it, whatever the type
  // attribute says, so textContent hands back the literal "&amp;" that escape
  // produced. Decoding once on the way in is what keeps "Duties & taxes
  // included" from reaching a shopper as "Duties &amp; taxes included".
  //
  // A textarea is the decoder because assigning to its innerHTML parses entities
  // without running anything: it is not a scripting container, so a value that
  // somehow carried markup cannot execute here.
  const decodeEntities = (text) => {
    if (!text || text.indexOf('&') === -1) return text || '';
    const box = document.createElement('textarea');
    box.innerHTML = text;
    return box.value;
  };

  // One line per country:
  //   CODE|std_lo|std_hi|exp_lo|exp_hi|std_price|exp_price|flag
  //
  // "-" in a day column disables that tier for that country — most lanes have no
  // express option worth quoting, and hiding the row whole is how they say so.
  //
  // A price is either a plain amount ("$10") or a weight ladder in kilograms:
  //
  //   1:17;2.5:31;4:46;*:56
  //
  // meaning up to 1 kg $17, up to 2.5 kg $31, up to 4 kg $46, above that $56.
  // The rungs mirror the weight conditions on the matching Shopify rate, so what
  // the panel quotes is what checkout charges. The trailing "*" rung is
  // mandatory: a cart heavier than the last rung would otherwise have no price
  // here and no rate at checkout either.
  //
  // The eighth field is a flag, not prose. "est" marks a lane the carrier prices
  // but will not commit a transit time to; the sentence a shopper reads for it
  // is a theme setting, so it can be edited and translated without regenerating
  // the table.
  //
  // Generated by scripts/shipping/build-delivery-table.mjs. Hand-editing it is
  // possible but the generator will overwrite it on the next rate change.
  const parseRates = (text) => {
    const table = {};
    decodeEntities(text).split('\n').forEach((line) => {
      const p = line.split('|').map((s) => s.trim());
      if (p.length < 5 || !p[0]) return;
      // A typo in a day column used to survive as NaN, and NaN != null, so the
      // row rendered with its arrival date silently collapsed onto the dispatch
      // day. Anything that is not a whole number is treated as absent instead.
      const num = (v) => {
        if (v === '-' || v === '') return null;
        const n = parseInt(v, 10);
        return Number.isFinite(n) ? n : null;
      };
      table[p[0].toUpperCase()] = {
        sl: num(p[1]), sh: num(p[2]),
        el: num(p[3]), eh: num(p[4]),
        sp: p[5] || '', ep: p[6] || '',
        est: (p[7] || '').toLowerCase() === 'est',
      };
    });
    return table;
  };

  /* --- Shipping prices in the currency the rest of the page is in --- *\
   *
   * The generated rates table is written in shop currency with a literal "$",
   * because that is what Shopify's zones charge. On a EUR storefront the PDP
   * therefore read "€167" for the jacket and "$25" for the shipping, two lines
   * apart — which looks broken, and leaves it genuinely ambiguous whether the
   * customer is about to be charged dollars or euros.
   *
   * Every other price on the page is a presentment-currency conversion of a
   * shop-currency figure, so converting this one is the consistent answer
   * rather than a special case. Shopify.currency.rate is the same rate it used
   * for those, and it is emitted by content_for_header; absent, the rate is 1
   * and the string passes through untouched, which is correct for a
   * single-currency store.
   *
   * Rounded to whole units because the source figures are whole dollars, and a
   * converted "€21.87" would imply a precision the zone price does not have.
   */
  const localizeMoney = (text) => {
    if (!text) return text;
    const cur = window.Shopify && window.Shopify.currency;
    const rate = parseFloat((cur && cur.rate) || 1) || 1;
    const active = (cur && cur.active) || null;
    if (!active || rate === 1) return text;

    let fmt;
    try {
      fmt = new Intl.NumberFormat(undefined, {
        style: 'currency', currency: active,
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      });
    } catch (e) { return text; }

    // Every "$1,234" or "$25" in the string, trailers included, so
    // "$17 · free $325+" converts on both halves.
    return text.replace(/\$\s?([\d,]+(?:\.\d+)?)/g, (whole, n) => {
      const v = parseFloat(String(n).replace(/,/g, ''));
      if (!Number.isFinite(v)) return whole;
      return fmt.format(Math.round(v * rate));
    });
  };

  // "1:17;2.5:31;*:56 · free over $325" -> the rung that covers `kg`, plus
  // whatever trailed the ladder. A price with no ";" or ":" is a flat one and
  // comes back unchanged, which is how the US line — a single Ops Engine rate
  // with no weight breaks — keeps working.
  const priceForWeight = (text, kg) => {
    if (!text) return '';
    const cut = text.indexOf(' · ');
    const ladder = cut === -1 ? text : text.slice(0, cut);
    const trailer = cut === -1 ? '' : text.slice(cut);
    if (ladder.indexOf(':') === -1) return text;

    const rungs = ladder.split(';').map((r) => {
      const at = r.indexOf(':');
      const ceiling = r.slice(0, at).trim();
      return {
        maxKg: ceiling === '*' ? Infinity : parseFloat(ceiling),
        price: r.slice(at + 1).trim(),
      };
    });
    // An unknown weight takes the top rung rather than the cheapest: quoting low
    // and charging high at checkout is the one failure this whole panel exists
    // to prevent.
    const weight = Number.isFinite(kg) ? kg : Infinity;
    const hit = rungs.find((r) => weight <= r.maxKg) || rungs[rungs.length - 1];
    return (hit.price.charAt(0) === '$' ? hit.price : '$' + hit.price) + trailer;
  };

  // "$10 · free $150+" -> { price: "$10", freeOver: "$150" }.
  //
  // The threshold leaves the price cell and becomes its own row, so the two have
  // to come apart after priceForWeight has resolved the ladder and BEFORE
  // localizeMoney converts the currency — otherwise the regex is matching
  // "€138" and the word "free" in whatever language the trailer was written in.
  // A price with no trailer returns itself and freeOver null, which is what
  // hides the row on the 132 lanes that have no free threshold at all.
  const splitFree = (text) => {
    if (!text) return { price: '', freeOver: null };
    const m = text.match(/\s*·\s*free\s*(\$\s?[\d,]+(?:\.\d+)?)\s*\+/i);
    if (!m) return { price: text, freeOver: null };
    return { price: (text.slice(0, m.index) + text.slice(m.index + m[0].length)).trim(), freeOver: m[1] };
  };

  const initDeliveryEstimator = () => {
    const root = document.querySelector('[data-delivery-estimator]');
    const urgency = document.querySelector('[data-delivery-urgency]');
    if (!root && !urgency) return;

    const cfg = root ? root.dataset : {};
    // Not `|| 13`: the schema's range starts at 0, and midnight is falsy, so a store that
    // dispatches at 00:00 was quietly run on a 1pm cutoff — wrong dispatch day, wrong dates,
    // wrong countdown, nothing in any log. Same trap CLAUDE.md names for `| default: true`.
    const cutoffRaw = parseInt(cfg.cutoffHour, 10);
    const cutoff = cutoffRaw >= 0 && cutoffRaw <= 23 ? cutoffRaw : 13;
    const tz = cfg.timezone || '';
    const market = cfg.country || 'US';
    // Days between the order landing and the parcel leaving the warehouse. The
    // carrier's transit windows start when it has the parcel, not when the
    // shopper clicks, and a 3PL takes a day or two to pick and inject.
    const leadDays = parseInt(cfg.leadDays, 10) || 0;
    const estimateNote = cfg.estimateNote || '';

    const ratesEl = root ? root.querySelector('[data-delivery-rates]') : null;
    const rates = parseRates(ratesEl ? ratesEl.textContent : '');
    // An explicit country wins; a ROW line, if the merchant wrote one, catches
    // the rest; otherwise the lane genuinely cannot be quoted.
    const ratesFor = (code) => rates[(code || '').toUpperCase()] || rates.ROW || null;

    const select = root ? root.querySelector('[data-dest-country]') : null;

    // ONE country for the whole feature — the panel, the Returns tab and the
    // line under the buy button — so they cannot name different places on the
    // same page. It starts at the market on every load and is never stored.
    let chosen = resolveCountry(market);

    // Recomputed rather than captured: a cutoff that passes mid-session has to
    // roll the dispatch day for the urgency line and the panel alike.
    const startFor = () => addBusinessDays(dispatchStart(cutoff, tz), leadDays);

    // Assigning textContent replaces the text node even when the string has not changed, and
    // [data-dest-answer] is aria-atomic — so re-rendering with identical values made a screen
    // reader re-read the entire answer. Tapping S, M, L on a product whose sizes share a
    // weight rung announced the same two lines three times.
    const setText = (el, next) => {
      if (el && el.textContent !== next) el.textContent = next;
    };

    // The panel's dates are recomputed on a timer as well as on interaction, so hoisted here:
    // the urgency line and the panel have to agree about which day the parcel leaves, and the
    // panel is defined further down, after the early return for a page with no picker.
    let renderPanel = () => {};

    // The cart weight this PDP would produce: one of whatever is selected. It is
    // what the weight ladder is read against, and it is honest for the order the
    // shopper is actually contemplating — a second item is checkout's business.
    //
    // Two sources, in order of trust.
    //
    // [data-weight-json] first: the section builds it, it holds only variants
    // that HAVE a weight, and its shape is ours rather than Shopify's.
    //
    // [data-variant-json] second, because that map can be absent from a theme
    // that is otherwise current. The GitHub integration applies files
    // independently, and it has already left this store serving a section
    // older than this file — every other file of the same push landed and
    // that one did not. The product JSON drop is on every PDP regardless of
    // how old the section is, so reading it keeps the ladder working through
    // that gap instead of quoting the top rung to everyone.
    const weightKg = () => {
      const pdp = document.querySelector('[data-pdp]') || document;
      const idInput = pdp.querySelector('[data-variant-id]');
      // Prices are flat store-wide, so priceForWeight returns a ladderless string
      // untouched and this never decides anything. Kept because the ladder code is
      // still the fallback if a weight-tiered table is ever fed in again.
      if (!idInput) return NaN;
      const id = String(idInput.value);

      const map = pdp.querySelector('[data-weight-json]');
      if (map) {
        try {
          const grams = JSON.parse(map.textContent || '{}')[id];
          if (grams > 0) return grams / 1000;
        } catch (e) {
          // Fall through: a malformed map is a reason to ask the other source,
          // not a reason to stop asking.
        }
      }

      const drop = pdp.querySelector('[data-variant-json]');
      if (!drop) return NaN;
      try {
        const list = JSON.parse(drop.textContent || '[]');
        const v = Array.isArray(list) && list.find((x) => x && String(x.id) === id);
        if (!v) return NaN;

        // Three shapes reach this, and `weight` does not mean the same thing in
        // all of them, so each is identified by a key only it has rather than
        // by guessing at the number.
        //
        //   grams + weight + weight_unit   /products/x.js — `weight` is 2.6 POUNDS
        //   weight + weight_in_unit + unit Liquid's drop — `weight` is 1180 grams
        //   weight                         what this store emits today: grams
        //
        // Read in that order. `grams` can only mean one thing. A unit is
        // believed only when weight_in_unit is beside it, because that pair is
        // unambiguous — whereas a unit sitting next to a bare `weight` cannot
        // say which of the two that number is, and either reading is wrong
        // somewhere: pounds-as-grams puts a 1.2 kg jacket on the 1 kg rung,
        // grams-as-pounds puts a t-shirt at 123 kg. A bare number falls
        // through to grams, which is what the Liquid docs promise and what
        // this catalogue actually carries.
        if (v.grams > 0) return v.grams / 1000;

        const PER_GRAM = { g: 1, gr: 1, grams: 1, kg: 1000, oz: 28.349523125, lb: 453.59237 };
        const unit = PER_GRAM[String(v.weight_unit || '').toLowerCase()];
        const inUnit = Number(v.weight_in_unit);
        if (unit && inUnit > 0) return (inUnit * unit) / 1000;

        return v.weight > 0 ? v.weight / 1000 : NaN;
      } catch (e) {
        return NaN;
      }
    };

    // ---- the rolling urgency line under the buy button ----------------------
    const urgencyText = urgency ? urgency.querySelector('[data-urgency-text]') : null;
    let timer = null;

    const renderUrgency = () => {
      if (!urgency || !urgencyText) return 0;
      const r = ratesFor(chosen);
      // No zone covers it, so there is no date to quote and no checkout to
      // reach either.
      if (!r || (r.el == null && r.sl == null)) { urgency.hidden = true; return 0; }
      // The soonest ANY lane could land it, because the line says "as soon as".
      //
      // Two ways this has been wrong. It read express's SLOWEST day once, which
      // understated every express country by the width of its window. Then it
      // read express's fastest whenever express existed — but express is not
      // always the earlier lane: Canada is Standard 7-12 against Priority 8-10,
      // so preferring express there gave up a day and pushed the line past the
      // suppression threshold below, costing Canada its line entirely.
      const fastest = Math.min(...[r.el, r.sl].filter((v) => v != null));

      // Two lanes this line has no business appearing on. An estimated lane is
      // one the carrier prices but will not commit to, and the caveat that says
      // so lives in the Shipping tab, which is collapsed — so the promise and
      // its disclaimer are never on screen together. And past a week, "order in
      // the next 3h 12m" is theatre: one dispatch day is noise against a
      // three-week transit. Seven is the boundary the old relative phrasing
      // used to stop naming a weekday, kept here as the suppression threshold.
      // We ship it, we will not put a date on it: an estimated lane is one the
      // carrier prices but will not commit to, and its caveat lives in the
      // Shipping tab, collapsed, so promise and disclaimer are never on screen
      // together. Past a week the countdown is theatre anyway -- one dispatch
      // day is noise against a three-week transit.
      if (r.est || fastest > 7) { urgency.hidden = true; return 0; }

      // One dispatch day drives both halves of the line: the countdown is the
      // deadline to make it, the date is what making it earns.
      const start = startFor();
      const by = addBusinessDays(start, fastest);
      // Counted to the CUTOFF, not to the dispatch day the lead time pushes the
      // parcel out to. The deadline the shopper can act on is the cutoff — the
      // lead time is what happens after they have made it, and rolls the arrival
      // date rather than the countdown.
      //
      // Always positive: the target rolls with the dispatch day, so passing the
      // cutoff restarts the clock against tomorrow instead of stalling.
      // Always under 24h, and always moving: the target is the next daily
      // cutoff, not the next dispatch day. `by` above is unaffected and still
      // counts business days, so the DATE stays honest even where the deadline
      // beside it is not.
      const nowZ = zonedNow(tz);
      const left = Math.ceil((dailyCutoff(cutoff, tz).getTime() - nowZ.getTime()) / 60000);
      const arrival = fmtArrival(by);

      // " in " only when the control is there to complete the sentence. Without
      // it the line would end on a dangling preposition on any product whose
      // section predates the markup -- the theme can be running a mix of
      // versions, so the section is not assumed to be as current as this file.

      // Two clauses, not one sentence with a subordinate clause hanging off it.
      // "Order within 17h 2m to receive as soon as Wed, Oct 21st" makes the
      // shopper hold the deadline in mind while parsing six more words before
      // the date lands; the comma splits it into the two facts it actually is.
      // "as soon as" also hedged a date the rest of the panel states flatly.
      // "in" rather than "within": a syllable shorter, and it reads as the
      // countdown it sits beside rather than as a deadline being imposed.
      if (left > 0) {
        setLine(urgencyText, [
          ['Order in ', false],
          [fmtCountdown(left), true],
          [', receive by ', false],
          [arrival, true],
        ]);
      } else {
        // Unreachable while dispatchStart and the target share a cutoff, but a
        // clock that cannot be trusted should drop the deadline, not guess.
        setLine(urgencyText, [
          ['Order today, receive by ', false],
          [arrival, true],
        ]);
      }
      urgency.hidden = false;
      return left;
    };

    // One clock for both halves of the feature, because they share one dispatch day.
    //
    // The countdown used to own the only timer, so when the cutoff passed mid-session it
    // re-targeted tomorrow's dispatch while the panel kept the dates it had computed on load.
    // The line under the buy button then promised a date a business day earlier than the
    // window in the Shipping tab, for the rest of the session — the same split setCountry
    // exists to close.
    let lastDispatch = '';
    const tick = () => {
      const day = dispatchStart(cutoff, tz).toDateString();
      if (day !== lastDispatch) {
        lastDispatch = day;
        renderPanel();
      }
      const left = renderUrgency();
      // The interval runs while EITHER half still needs it: the countdown while it has time
      // left to show, and the panel always, because its dates roll at the cutoff whether or
      // not this country gets a countdown.
      if (!timer && (left > 0 || root)) {
        timer = setInterval(tick, 60000);
      } else if (timer && left <= 0 && !root) {
        clearInterval(timer);
        timer = null;
      }
    };

    // Without this a backgrounded tab is restored showing a stale number, which is worse
    // than showing no countdown at all.
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) tick();
    });

    if (!root) {
      tick();
      return;
    }

    // ---- the panel in the Shipping tab --------------------------------------
    const results = root.querySelector('[data-dest-results]');
    const error = root.querySelector('[data-dest-error]');
    const noteEl = root.querySelector('[data-dest-note]');
    const rowFor = (k) => root.querySelector('[data-dest-row="' + k + '"]');

    // An estimated lane is quoted in weeks, not as a date range. "Sep 14 – Sep
    // 28" is typographically identical to a guaranteed "Sep 1 – Sep 7", and
    // those two numbers did not come from the carrier — its Remote sheet has no
    // timeline row at all. Weeks carry the same information at the precision we
    // actually have.
    const fmtWeeks = (lo, hi) => {
      const a = Math.max(1, Math.round(lo / 5));
      const b = Math.max(a, Math.round(hi / 5));
      return a === b ? `about ${a} week${a === 1 ? '' : 's'}` : `${a}–${b} weeks`;
    };

    // A tier renders only when it has BOTH a window and a price. Half a row — a
    // service name with an empty cell beside it — reads as broken rather than as
    // unavailable, and the generated table cannot produce one, but the
    // delivery_rates override can: parseRates accepts a five-field row, which
    // leaves both prices empty.
    // Collected by tier() as it renders, read by render() once both have run.
    // The threshold is the same on both services where both carry one, so the
    // standard lane wins and express only speaks when standard is silent —
    // which is also the order they are quoted in.
    let freeOver = null;

    const tier = (key, lo, hi, price, est, dateSel, priceSel) => {
      const row = rowFor(key);
      if (!row) return false;
      const dateEl = root.querySelector(dateSel);
      const priceEl = root.querySelector(priceSel);
      const split = splitFree(priceForWeight(price, weightKg()));
      const amount = localizeMoney(split.price);
      if (lo == null || hi == null || !amount) { row.hidden = true; return false; }
      setText(dateEl, est ? fmtWeeks(lo, hi) : fmtWindow(startFor(), lo, hi));
      setText(priceEl, amount);
      row.hidden = false;
      if (!freeOver && split.freeOver) freeOver = localizeMoney(split.freeOver);
      return true;
    };

    // The free-shipping row. Hidden rather than emptied: unlike .dest__note it
    // is a promise, not a caveat, and a promise that flickers in and out of an
    // aria-live region on every variant change is worse than one announced once
    // with the rest of the answer.
    const freeEl = root.querySelector('[data-dest-free]');
    const freeTpl = (freeEl && freeEl.dataset.template) || 'Free shipping over {amount}';

    const render = () => {
      freeOver = null;
      const r = ratesFor(chosen);
      // No lane covers this country, so there is no rate to quote and no
      // checkout to reach. Saying so beats inventing a date.
      if (!r) {
        results.hidden = true;
        setText(noteEl, '');
        if (freeEl) freeEl.hidden = true;
        if (error) error.hidden = false;
        return;
      }
      if (error) error.hidden = true;
      // Both calls must run — each owns its row's visibility — so the results
      // are collected before being combined rather than short-circuited.
      const hasStd = tier('standard', r.sl, r.sh, r.sp, r.est, '[data-dest-standard]', '[data-dest-standard-price]');
      const hasExp = tier('express', r.el, r.eh, r.ep, r.est, '[data-dest-express]', '[data-dest-express-price]');
      const shown = hasStd || hasExp;
      results.hidden = !shown;
      if (!shown && error) error.hidden = false;
      // The caveat for an estimated lane. The sentence is a theme setting rather
      // than a column in the generated table, so it can be edited and translated
      // without regenerating 144 rows. textContent, not innerHTML.
      setText(noteEl, shown && r.est ? estimateNote : '');
      if (freeEl) {
        // Only where a lane actually carries a threshold. The row is drawn by
        // the hairline above it, so a stray empty one would leave a rule
        // floating under the table with nothing beneath it.
        const on = shown && !!freeOver;
        if (on) setText(freeEl, freeTpl.replace('{amount}', freeOver));
        freeEl.hidden = !on;
      }
    };

    renderPanel = render;

    // One entry point, so the line under the buy button and the panel in the tab
    // can never disagree about which country they are quoting.
    const setCountry = (code) => {
      chosen = (code || '').toUpperCase();
      // `chosen`, not `code`: a lowercase code would match no <option> and blank the
      // picker while the panel below it quoted correctly.
      if (select) select.value = chosen;
      render();
      // tick, not renderUrgency: the interval is only ever armed inside tick, and calling
      // renderUrgency directly put a live-looking countdown on screen that never counted.
      tick();
    };

    if (select) {
      // data-country is localization.country.iso_code — the footer's Shipping
      // Country, which is where every load starts.
      select.value = chosen;
      // Resolves on load rather than behind an Apply click: the country is
      // already known from the market, so there is nothing to wait for.
      render();
      lastDispatch = dispatchStart(cutoff, tz).toDateString();

      select.addEventListener('change', () => {
        // Persisted here and only here — this is the shopper actually choosing.
        setCountry(select.value);
        // Keeps the Returns picker in step — one shopper, one country.
        document.dispatchEvent(new CustomEvent('fr:country', { detail: select.value }));
      });

    }

    // Hoisted out of the picker's own block, and compared against `chosen`
    // rather than the picker's value. Inside it, the line's control could only
    // follow the Returns picker while the Shipping tab's picker also existed —
    // and setCountry moves both regardless, so one listener is all that is
    // wanted. Two would re-render for a value already applied.
    document.addEventListener('fr:country', (e) => {
      if (e.detail && e.detail.toUpperCase() !== chosen) setCountry(e.detail);
    });

    // The price depends on the selected variant's weight, so a size change that
    // crosses a weight rung has to re-quote. initVariantSelectors dispatches
    // this from the option group and it bubbles to document; initPdpVariants
    // runs first and has already written the new id into [data-variant-id].
    document.addEventListener('variant:change', () => render());

    // Started last, so its first pass finds both halves built.
    tick();
  };

  /* --- PDP Returns resolved against the shopper's country --- */
  // The refund policy is regional — offered in the USA, "soon" for EU/UK/CA/AUS,
  // unavailable elsewhere — so the panel cannot state one set of terms and be
  // right. A country with no line is treated as not-yet-offered rather than
  // falling back to the US terms, which would promise a return that isn't there.
  const initReturnsByCountry = () => {
    const panel = document.querySelector('[data-returns-panel]');
    if (!panel) return;

    const select = panel.querySelector('[data-returns-country]');
    const rulesEl = panel.querySelector('[data-returns-rules]');
    const terms = panel.querySelector('[data-returns-terms]');
    const intro = panel.querySelector('[data-returns-intro]');
    const none = panel.querySelector('[data-returns-none]');
    const start = panel.querySelector('[data-returns-start]');

    // CODE|window days|exchange|store credit|to card
    // Decoded on read for the same reason the rates table is: this comes through
    // Liquid's `escape` into a <script>, which is raw text, so an ampersand in a
    // cost would otherwise reach the shopper as "&amp;".
    const rules = {};
    decodeEntities((rulesEl && rulesEl.textContent) || '').split('\n').forEach((line) => {
      const p = line.split('|').map((s) => s.trim());
      if (p.length < 5 || !p[0]) return;
      rules[p[0].toUpperCase()] = { days: p[1], ex: p[2], cr: p[3], cd: p[4] };
    });
    const ruleFor = (c) => rules[(c || '').toUpperCase()] || rules.ROW || null;

    const set = (sel, val) => {
      const el = panel.querySelector(sel);
      if (el) el.textContent = val || '';
    };

    const render = (code) => {
      const r = ruleFor(code);
      if (terms) terms.hidden = !r;
      if (intro) intro.hidden = !r;
      if (none) none.hidden = !!r;
      // The primary action goes with the terms: offering "Start a return" to a
      // region that cannot return is the one link worse than no link. The
      // policy link stays - it is what explains the regional difference.
      if (start) start.hidden = !r;
      if (!r) return;
      // Cheapest first, which is the order the markup is in too: an exchange is
      // free, store credit is free, and only the card refund costs anything.
      set('[data-returns-exchange]', r.ex);
      set('[data-returns-credit]', r.cr);
      set('[data-returns-card]', r.cd);
      if (intro) {
        // Carries the fallback because Liquid cannot: a literal {days} inside a
        // {{ }} output tag fails to parse, so the section ships the setting
        // bare and the default belongs here.
        const tpl = intro.dataset.template ||
          'You have {days} days from the date of delivery to return or exchange items.';
        intro.textContent = tpl.replace('{days}', r.days);
      }
    };

    if (select) {
      // Read before anything overrides it: the option Liquid marked selected is
      // localization.country.iso_code, i.e. the footer's Shipping Country.
      const market = select.value;
      const initial = resolveCountry(market);
      select.value = initial;
      render(initial);

      select.addEventListener('change', () => {
        render(select.value);
        // Shipping and Returns answer the same question about the same person.
        // Setting the country in one tab has to move the other, or the page
        // quietly holds two different answers.
        document.dispatchEvent(new CustomEvent('fr:country', { detail: select.value }));
      });

      document.addEventListener('fr:country', (e) => {
        if (e.detail && e.detail !== select.value) {
          select.value = e.detail;
          render(e.detail);
        }
      });
    } else {
      // THE MERGED CASE, and now the normal one: Returns sits under Shipping in
      // one panel and has no picker of its own, because asking the same country
      // twice let the two answers drift apart. Seed from the Shipping picker —
      // initDeliveryEstimator runs first in init(), so its select already holds
      // the resolved country rather than the raw market default — then follow
      // it for the rest of the session.
      const shipSelect = document.querySelector('[data-dest-country]');
      const seed = shipSelect && shipSelect.value
        ? shipSelect.value
        : resolveCountry((document.documentElement.lang || 'US').slice(-2).toUpperCase());
      render(seed);

      document.addEventListener('fr:country', (e) => {
        if (e.detail) render(e.detail);
      });
    }
  };

  /* --- Shop Promise: the few rules the document cannot reach --- */
  // Everything tokens and inheritance can do is in component-product-page.css.
  // What is left needs a stylesheet INSIDE the shadow root, because it either
  // targets an internal class or has to outrank an inline style the widget's
  // own JS writes:
  //
  //   padding    — .ContainerCommon__Container reads --p-space-350, which is
  //                also the expanded zip form's row gap. Zeroing the token
  //                would flatten the form; zeroing the class leaves it alone.
  //   alignment  — the rows are flex, aligned by --ui-*-align written inline.
  //                Only !important beats an inline declaration.
  //
  // Selectors match on the component half of each class ([class*=]) rather than
  // the full string. These are webpack module names — stable in shape, not
  // guaranteed verbatim across Shopify's rebuilds — so matching the half that
  // carries the meaning survives a rehash of the rest.
  const PROMISE_SKIN = `
    [class*="ContainerCommon__Container"] {
      padding: 0 !important;
      font-size: inherit !important;
    }
    [class*="Text__Text"] {
      font-size: inherit !important;
      letter-spacing: inherit !important;
    }
    [class*="ShopPromiseLogo__Logo"] { vertical-align: middle; }
    /* The collapsed line follows the buy column: centred on mobile, flush left
       from the same 769px the rest of the PDP turns at. The expanded zip form is
       left alone — it is a form, and a centred form reads as a mistake. */
    [class*="PromiseShell__Promise"] [class*="BlockStack__BlockStack"],
    [class*="PromiseShell__Promise"] [class*="InlineStack__InlineStack"] {
      --ui-block-stack-align: center !important;
      --ui-inline-stack-align: center !important;
    }
    [class*="ExpansionBlock__ExpansionBlock"] [class*="BlockStack__BlockStack"],
    [class*="ExpansionBlock__ExpansionBlock"] [class*="InlineStack__InlineStack"] {
      --ui-block-stack-align: start !important;
      --ui-inline-stack-align: start !important;
    }
    @media (min-width: 769px) {
      [class*="PromiseShell__Promise"] [class*="BlockStack__BlockStack"],
      [class*="PromiseShell__Promise"] [class*="InlineStack__InlineStack"] {
        --ui-block-stack-align: start !important;
        --ui-inline-stack-align: start !important;
      }
    }
  `;

  // The box carries a live carrier estimate; our urgency line is derived from
  // the theme's rates table. Two delivery dates on one page invites the shopper
  // to notice they disagree, so when Shopify's resolves, ours steps aside.
  const initShopPromise = () => {
    const host = () => document.querySelector('delivery-promise-wc');
    if (!host() && !document.querySelector('.delivery-promise__promise-container')) return;
    const urgency = document.querySelector('[data-delivery-urgency]');

    // Appended last so it follows the widget's own stylesheets; every rule that
    // has to win says so explicitly anyway.
    const skin = () => {
      const el = host();
      const sr = el && el.shadowRoot;
      if (!sr || sr.querySelector('[data-fr-skin]')) return;
      const style = document.createElement('style');
      style.setAttribute('data-fr-skin', '');
      style.textContent = PROMISE_SKIN;
      sr.appendChild(style);
    };

    // Presence is not enough on either count. <delivery-promise-wc> mounts empty
    // and stays empty on products no promise covers, so hiding our line on the
    // element alone would drop it from PDPs that never get a box at all.
    //
    // Nor is shadowRoot.textContent: the widget ships nineteen <style> elements
    // inside the shadow root, so that string is never blank and the check it
    // backed was only ever passing by way of the offsetHeight test beside it.
    // Read the rendered leaves instead.
    const resolved = () => {
      const el = host();
      if (!el || !el.offsetHeight) return false;
      const sr = el.shadowRoot;
      if (!sr) return false;
      return [...sr.querySelectorAll('*')].some(
        (n) => !/^(style|script)$/i.test(n.tagName) && n.childElementCount === 0 && n.textContent.trim()
      );
    };

    // Toggle a class, never the hidden attribute — [hidden] is overridden by a
    // display rule in component-product-page.css and would not take effect.
    const sync = () => {
      skin();
      if (urgency) urgency.classList.toggle('is-superseded', resolved());
    };

    sync();
    // The bundle is deferred and resolves well after this runs, so a one-shot
    // check would almost always miss it.
    new MutationObserver(sync).observe(document.body, { childList: true, subtree: true });
  };

  /* --- Overlay copy: pick salt or slate from the image underneath it --- */
  const initOverlayContrast = () => {
    // Every place copy is laid over an image. `band` is the strip the copy
    // actually sits over — Complete the Look's heading sits in the top-left —
    // and sampling the wrong end is how white text ends up on a pale sky.
    //
    // Foreign Engineering used to be here too. Its copy now sits on white
    // below the image, so there is nothing to measure; leaving it in would
    // also set crossOrigin on four PDP images for an answer nobody reads.
    const targets = [
      { host: '.ctl__lookbook', img: '.ctl__lookbook-img', band: 'top' },
    ];

    // Sample that corner and measure its luminance, so the text flips to slate
    // on pale imagery and stays salt on dark.
    const measure = (host, imgSel, band) => {
      const img = host.querySelector(imgSel);
      if (!img) return;

      const sample = () => {
        try {
          const c = document.createElement('canvas');
          const w = 24, h = 24;
          c.width = w; c.height = h;
          const ctx = c.getContext('2d');
          const bandH = img.naturalHeight * 0.3;
          const sy = band === 'top' ? 0 : Math.max(0, img.naturalHeight - bandH);
          ctx.drawImage(img, 0, sy, img.naturalWidth * 0.5, bandH, 0, 0, w, h);
          const d = ctx.getImageData(0, 0, w, h).data;
          let sum = 0;
          for (let i = 0; i < d.length; i += 4) {
            sum += 0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2];
          }
          const avg = sum / (d.length / 4);
          host.classList.toggle('is-light-bg', avg > 140);
        } catch (e) {
          // Tainted canvas or a blocked image: keep the salt default
        }
      };

      img.crossOrigin = 'anonymous';
      if (img.complete && img.naturalWidth) sample();
      else img.addEventListener('load', sample, { once: true });
    };

    targets.forEach((t) => {
      document.querySelectorAll(t.host).forEach((host) => measure(host, t.img, t.band));
    });
  };

  /* --- Klaviyo back-in-stock ---------------------------------------------

     The button a shopper sees on a sold-out variant is the theme's
     (.pdp__notify / .pdp-band__notify, styled off the add-to-cart button's own
     rules). The flow behind it is still Klaviyo's.

     We used to restyle Klaviyo's injected trigger instead. That meant fighting
     unstable class names and inline styles from an app that re-renders on its
     own schedule, and it broke three times. So its button is clipped where it
     stands and its click is proxied.

     Two rules this code exists to honour:
       - Park, never remove. The node is clipped in place. Moving it can sever a
         handler delegated from a Klaviyo ancestor; removing it can do worse.
       - Look the trigger up at click time, never cache it. Klaviyo re-renders,
         and a cached node is a node that has already gone stale.
  ------------------------------------------------------------------------- */

  const BIS_CLASS = /klaviyo[-_]?bis|bis[-_]?trigger|back[-_]?in[-_]?stock/i;
  const BIS_TEXT = /^(notify me|get notified|email me when|notify when)/i;

  const isBisNode = (el) => {
    // Never match our own button, the band's buy button, or anything inside them.
    if (el.closest('[data-notify]') || el.closest('[data-mobile-submit]')) return false;
    const cls = (el.getAttribute('class') || '') + ' ' + (el.id || '');
    if (BIS_CLASS.test(cls)) return true;
    return BIS_TEXT.test((el.textContent || el.value || '').trim().toLowerCase());
  };

  // Live lookup. Returns Klaviyo's trigger or null — never a remembered node.
  const findBisTrigger = () => {
    // Wider than button/a/input: Klaviyo's form embed renders its trigger as a
    // div or a [role=button], which the old selector list missed entirely and
    // is the likeliest reason the skin never landed.
    const sel = 'button, a, input[type="button"], input[type="submit"], [role="button"], div, span';
    const hits = [];
    document.querySelectorAll(sel).forEach((el) => { if (isBisNode(el)) hits.push(el); });
    // Keep only the innermost matches, so we act on the button and not on some
    // wrapper three levels up that happens to contain its text.
    const inner = hits.filter((el) => !hits.some((o) => o !== el && el.contains(o)));
    return inner[0] || null;
  };

  const initKlaviyoBisPark = () => {
    const park = () => {
      // Never hide Klaviyo's button unless ours is actually on the page. The
      // two live in different files and ship separately — CLAUDE.md records the
      // GitHub integration applying theme files independently and refusing
      // sections/product-info.liquid three pushes running, while assets land
      // within seconds. Without this guard a refused section leaves the CSS and
      // JS hiding Klaviyo's button with no replacement rendered, and a sold-out
      // product has no way to sign up at all — with nothing on the page looking
      // wrong. Half a deploy must degrade, not delete.
      if (!document.querySelector('[data-notify]')) return;
      const t = findBisTrigger();
      if (!t || t.classList.contains('fr-bis-parked')) return;
      t.classList.add('fr-bis-parked');
      t.setAttribute('data-fr-bis', '');
      // Out of the tab order and out of the a11y tree while it is 1px square;
      // openBis() hands both back if it ever has to reveal it.
      t.setAttribute('tabindex', '-1');
      t.setAttribute('aria-hidden', 'true');
    };

    park();
    // document.body, not [data-pdp]: Klaviyo injects where it likes, and the
    // old PDP-scoped observer could not see it when it landed outside.
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; park(); });
    }).observe(document.body, { childList: true, subtree: true });
  };

  /* --- Notify me: our button, Klaviyo's flow --- */
  const initPdpNotify = () => {
    const pdp = document.querySelector('[data-pdp]');
    if (!pdp) return;

    const buttons = pdp.querySelectorAll('[data-notify]');
    if (!buttons.length) return;

    const say = (text) => {
      pdp.querySelectorAll('[data-notify-msg]').forEach((el) => { el.textContent = text; });
    };

    const reveal = (node) => {
      node.classList.add('fr-bis-revealed');
      node.removeAttribute('aria-hidden');
      node.removeAttribute('tabindex');
      node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      try { node.focus(); } catch (e) {}
    };

    // Klaviyo injects late, and a shopper can reach a sold-out variant and click
    // before it lands — so wait a beat rather than failing on the first look.
    const waitForTrigger = (ms) => new Promise((resolve) => {
      const started = Date.now();
      const tick = () => {
        const t = findBisTrigger();
        if (t) return resolve(t);
        if (Date.now() - started >= ms) return resolve(null);
        setTimeout(tick, 100);
      };
      tick();
    });

    const openBis = async (btn) => {
      say('');
      const label = btn.textContent;
      btn.setAttribute('aria-busy', 'true');

      try {
        const trigger = await waitForTrigger(1500);

        // 1. The real thing.
        if (trigger) { trigger.click(); return; }

        // 2. Klaviyo's onsite queue, the same API the newsletter identify uses.
        const formId = pdp.dataset.notifyFormId;
        if (formId) {
          try {
            window.klaviyo = window.klaviyo || [];
            window.klaviyo.push(['openForm', formId]);
            await new Promise((r) => setTimeout(r, 600));
            if (document.querySelector('[class*="klaviyo-form"]')) return;
          } catch (e) {}
        }

        // 3. Anything parked goes back on screen. Klaviyo's own unstyled button
        //    appearing is a far better failure than a button that does nothing.
        const parked = document.querySelector('[data-fr-bis]');
        if (parked) { reveal(parked); return; }

        // 4. Out of routes. Say so, rather than looking like it worked.
        say(pdp.dataset.notifyUnavailable || 'Back-in-stock alerts are unavailable right now — please try again shortly.');
        console.warn('[FR notify] no Klaviyo trigger, form id or parked node; nothing to open.');
      } finally {
        btn.removeAttribute('aria-busy');
        btn.textContent = label;
      }
    };

    buttons.forEach((btn) => btn.addEventListener('click', () => openBis(btn)));

    // Watchdog. If Klaviyo is blocked outright there is no trigger to click, no
    // widget to reveal and no queue to push to — so offering the button at all
    // is a lie. Hand the row back to the disabled "Sold out" button, which is
    // exactly the behaviour before any of this existed.
    setTimeout(() => {
      if (findBisTrigger() || document.querySelector('[data-fr-bis]')) return;
      if (pdp.dataset.notifyFormId || window.klaviyo || window._learnq) return;
      console.info('[FR notify] Klaviyo never loaded; restoring the sold-out button.');
      pdp.querySelectorAll('[data-notify]').forEach((el) => { el.hidden = true; });
      pdp.dataset.notifyDead = 'true';
      const addBtn = pdp.querySelector('[data-add-btn]');
      const mobileBtn = pdp.querySelector('[data-mobile-submit]');
      if (addBtn && addBtn.disabled) addBtn.hidden = false;
      if (mobileBtn && mobileBtn.disabled) mobileBtn.hidden = false;
    }, 8000);
  };

  /* --- PDP sticky band (mobile): hide once past Foreign Engineering --- */
  const initPdpStickyBand = () => {
    const band = document.querySelector('[data-sticky-band]');
    if (!band) return;

    // The band must never sit on top of full-bleed imagery, and a fixed element
    // cannot both cross a section and stay clear of it — so it retires the
    // moment its boundary reaches the viewport and returns if you scroll back
    // up into the product details.
    //
    // The resting-area section is that boundary when present, which keeps the
    // handover a placeable decision rather than one wired to whichever section
    // happens to come first. The named sections remain as a fallback for
    // templates that predate it.
    const boundary =
      document.querySelector('[data-band-rest]') ||
      document.querySelector('.section-fr-engineering') ||
      document.querySelector('.section-pdp-complete-the-look');
    if (!boundary) return;

    const syncHeight = () => {
      document.documentElement.style.setProperty('--pdp-band-h', band.offsetHeight + 'px');
    };
    syncHeight();
    if (window.ResizeObserver) new ResizeObserver(syncHeight).observe(band);

    let ticking = false;
    const place = () => {
      ticking = false;
      // Hide as soon as the section's first pixel crosses the viewport bottom,
      // which is exactly where the band's own top edge sits.
      const top = boundary.getBoundingClientRect().top;
      band.classList.toggle('is-hidden', top <= window.innerHeight);
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(place);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    place();
  };

  /* --- PDP zoom: in-place on desktop, fullscreen lightbox on touch --- */
  const initPdpZoom = () => {
    const gallery = document.querySelector('[data-product-gallery]');
    const zoom = document.querySelector('[data-pdp-zoom]');
    if (!gallery || !zoom) return;

    const img = zoom.querySelector('[data-zoom-img]');
    const stage = zoom.querySelector('[data-zoom-stage]');
    const cursor = gallery.querySelector('[data-zoom-cursor]');
    const main = gallery.querySelector('[data-gallery-main]');

    // Re-evaluated per call, not cached: a resize or a hybrid device must not
    // get stuck on whichever path happened to be true at load.
    const isDesktop = () => window.matchMedia('(min-width: 769px) and (pointer: fine)').matches;

    /* ---- Desktop: magnify inside the gallery frame, pan with the pointer.
       The page is never covered and there is nothing to exit out of. ---- */
    let zoomedSlide = null;

    // The point under the cursor is the one that stays put as the image scales,
    // which is what makes the pan feel like moving a loupe over the fabric.
    const originFromEvent = (slide, e) => {
      const r = slide.getBoundingClientRect();
      const clamp = (n) => Math.max(0, Math.min(100, n));
      return clamp(((e.clientX - r.left) / r.width) * 100) + '% '
           + clamp(((e.clientY - r.top) / r.height) * 100) + '%';
    };

    const slideImg = (slide) => slide && slide.querySelector('.pdp__gallery-img');

    const zoomIn = (slide, e) => {
      const el = slideImg(slide);
      if (!el) return;
      // 2x over a ~58vw column needs the 2048 source. srcset has to go or the
      // browser re-picks the 1200w candidate and the detail stays mush. Flagged
      // rather than compared against el.src: image_url emits a protocol-relative
      // URL, so el.src (resolved, absolute) would never match it.
      if (slide.dataset.zoomSrc && !el.dataset.zoomLoaded) {
        el.dataset.zoomLoaded = '1';
        el.srcset = '';
        el.src = slide.dataset.zoomSrc;
      }
      el.style.transformOrigin = originFromEvent(slide, e);
      slide.classList.add('is-zoomed');
      zoomedSlide = slide;
      if (cursor) cursor.textContent = '−';
    };

    const zoomOut = () => {
      if (!zoomedSlide) return;
      const el = slideImg(zoomedSlide);
      // The hi-res src stays put — it is already downloaded, and swapping back
      // would flash on every re-zoom.
      if (el) el.style.transformOrigin = '';
      zoomedSlide.classList.remove('is-zoomed');
      zoomedSlide = null;
      if (cursor) cursor.textContent = '+';
    };

    // Cursor-following +/- over the gallery, and the pan while zoomed
    // (desktop, fine pointers only)
    if (window.matchMedia('(min-width: 769px) and (pointer: fine)').matches) {
      gallery.addEventListener('pointermove', (e) => {
        if (cursor) {
          const r = gallery.getBoundingClientRect();
          cursor.style.transform = 'translate(' + (e.clientX - r.left) + 'px,' + (e.clientY - r.top) + 'px)';
        }
        if (zoomedSlide) {
          const el = slideImg(zoomedSlide);
          if (el) el.style.transformOrigin = originFromEvent(zoomedSlide, e);
        }
      }, { passive: true });

      // Fetch the 2048 ahead of the click so the first zoom doesn't stutter.
      gallery.addEventListener('pointerover', (e) => {
        const slide = e.target.closest('[data-gallery-slide]');
        if (!slide || slide.dataset.zoomPreloaded || !slide.dataset.zoomSrc) return;
        slide.dataset.zoomPreloaded = '1';
        new Image().src = slide.dataset.zoomSrc;
      }, { passive: true });
    }

    /* ---- Touch: the fullscreen viewer, since there is no hover to pan with ---- */
    // inset: open as a panel floating over a dimmed page rather than replacing
    // the page outright. The gallery's touch viewer wants the full bleed — it
    // is a pan-and-zoom surface — but a fabric swatch is being compared against
    // the product it belongs to, so the page has to stay visible behind it.
    const open = (slide, inset) => {
      if (!slide || !img) return;
      img.src = slide.dataset.zoomSrc || '';
      img.alt = slide.dataset.zoomAlt || '';
      zoom.classList.toggle('pdp-zoom--inset', !!inset);
      zoom.classList.add('is-active');
      zoom.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    };

    const close = () => {
      zoom.classList.remove('is-active', 'is-zoomed');
      zoom.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      // Or the next image opens already magnified at the last pointer position.
      clearInsetZoom();
    };

    // Mobile swipes share this surface, so a drag must not count as a click.
    let downX = 0, downY = 0;
    gallery.addEventListener('pointerdown', (e) => { downX = e.clientX; downY = e.clientY; }, { passive: true });
    gallery.addEventListener('click', (e) => {
      const slide = e.target.closest('[data-gallery-slide]');
      if (!slide) return;
      if (Math.abs(e.clientX - downX) > 10 || Math.abs(e.clientY - downY) > 10) return;
      if (!isDesktop()) { open(slide); return; }
      if (zoomedSlide === slide) { zoomOut(); return; }
      zoomOut();
      zoomIn(slide, e);
    });

    // Anything else on the page that wants the full-screen viewer: the fabric
    // swatches in the Fabric tab today. Same contract as a gallery slide —
    // data-zoom-src and data-zoom-alt — so nothing new has to be taught here.
    //
    // Straight to open(), never the isDesktop() fork above: that branch runs
    // the in-place magnifier, which needs a .pdp__gallery-img child and
    // gallery-relative pointer geometry a fabric swatch does not have.
    //
    // Bound on document, so it also covers nodes moved between panels after
    // load. It does inherit this function's early return — no gallery on the
    // page means no lightbox markup either, so there is nothing to open.
    //
    // Same 10px drag threshold the gallery uses. The fabric swatches sit in a
    // static grid and never needed it, but Foreign Engineering's cards live in
    // a horizontal scroller on mobile, where a swipe that happens to end on a
    // card would otherwise open the viewer on top of the fling.
    let zDownX = 0, zDownY = 0;
    document.addEventListener('pointerdown', (e) => {
      zDownX = e.clientX; zDownY = e.clientY;
    }, { passive: true, capture: true });

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-zoom-open]');
      if (!trigger) return;
      // e.detail is 0 for a click synthesised by Enter or Space, which also
      // reports clientX/Y of 0 — measuring that against the last pointerdown
      // would make the button openable by mouse only.
      if (e.detail > 0
        && (Math.abs(e.clientX - zDownX) > 10 || Math.abs(e.clientY - zDownY) > 10)) return;
      e.preventDefault();
      open(trigger, true);
    });

    // Release the zoom rather than let a magnified frame snap past.
    gallery.addEventListener('pointerleave', zoomOut);
    if (main) main.addEventListener('scroll', zoomOut, { passive: true });

    // Lightbox-internal zoom. It must not touch the gallery's cursor chip \u2014
    // that belongs to the in-place zoom now, and a hybrid device using the
    // lightbox would otherwise be left showing a stale minus over the gallery.
    const setZoomed = (on) => {
      zoom.classList.toggle('is-zoomed', on);
      if (on && stage) {
        // Centre the pan on the point that was zoomed into
        stage.scrollLeft = (stage.scrollWidth - stage.clientWidth) / 2;
        stage.scrollTop = (stage.scrollHeight - stage.clientHeight) / 2;
      }
    };

    const zin = zoom.querySelector('[data-zoom-in]');
    const zout = zoom.querySelector('[data-zoom-out]');
    if (zin) zin.addEventListener('click', () => setZoomed(true));
    if (zout) zout.addEventListener('click', () => setZoomed(false));
    /* --- Inset panel: magnify under the pointer, like the gallery ---
       The frame holds still and the image scales inside it. Mapping the pointer
       to an origin needs the image's UNSCALED box, so it is captured at the
       moment of zooming in — once scale(2) is on, getBoundingClientRect reports
       the magnified box and the mapping would drift as you move.

       Fine pointers only, mirroring the gallery: touch has nothing to follow and
       keeps the scroll-pan. */
    const finePointer = () => window.matchMedia('(min-width: 769px) and (pointer: fine)').matches;
    let insetFrame = null;

    const insetOrigin = (e) => {
      if (!insetFrame || !img) return;
      const clamp = (n) => Math.max(0, Math.min(100, n));
      img.style.transformOrigin =
        clamp(((e.clientX - insetFrame.left) / insetFrame.width) * 100) + '% ' +
        clamp(((e.clientY - insetFrame.top) / insetFrame.height) * 100) + '%';
    };

    const clearInsetZoom = () => {
      insetFrame = null;
      if (img) img.style.transformOrigin = '';
    };

    if (stage) stage.addEventListener('pointermove', (e) => {
      if (!insetFrame) return;
      if (!zoom.classList.contains('is-zoomed')) return;
      insetOrigin(e);
    }, { passive: true });

    if (stage) stage.addEventListener('click', (e) => {
      if (e.target === img) {
        const on = !zoom.classList.contains('is-zoomed');
        if (zoom.classList.contains('pdp-zoom--inset') && finePointer()) {
          if (on) { insetFrame = img.getBoundingClientRect(); insetOrigin(e); }
          else clearInsetZoom();
        }
        setZoomed(on);
        return;
      }
      // Everything around the image is scrim in inset mode, and a scrim you can
      // see the page through reads as dismissable — so it is. The full-bleed
      // viewer has no "outside" to click, which is why this is scoped.
      if (zoom.classList.contains('pdp-zoom--inset')) close();
    });

    zoom.querySelectorAll('[data-zoom-close]').forEach(b => b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (zoom.classList.contains('is-active')) close();
      zoomOut();
    });
  };

  /* --- PDP Gallery (swipe/scroll + progress bar) --- */
  const initPdpGallery = () => {
    const gallery = document.querySelector('[data-product-gallery]');
    if (!gallery) return;
    const main = gallery.querySelector('[data-gallery-main]');
    const allSlides = Array.from(gallery.querySelectorAll('[data-gallery-slide]'));
    const fill = gallery.querySelector('[data-gallery-progress]');
    const progress = gallery.querySelector('.pdp__gallery-progress');
    if (!main || !allSlides.length) return;

    let current = 0;

    // Counted, not captured. Choosing a colourway hides the slides belonging to
    // the other colours, so a count taken once at load would leave the progress
    // bar describing a gallery that is no longer there.
    let visible = allSlides;
    const recount = () => {
      visible = allSlides.filter((s) => !s.hasAttribute('hidden'));
      // One image is not a gallery; a permanently full bar just announces one.
      if (progress) progress.hidden = visible.length < 2;
    };
    recount();

    // Desktop stacks the images and scrolls vertically; mobile swipes horizontally.
    const isVertical = () => window.matchMedia('(min-width: 769px)').matches;

    // One bar, two orientations. The CSS swaps which axis is 2px and where the
    // fill is anchored; this has to swap the matching axis or the bar sits at 0
    // forever — scaleX on a 2px-wide track changes nothing visible.
    const setFill = (frac) => {
      if (!fill) return;
      const clamped = Math.min(1, Math.max(0, frac));
      fill.style.transform = (isVertical() ? 'scaleY(' : 'scaleX(') + clamped + ')';
    };

    const syncUI = (idx) => setFill(visible.length ? (idx + 1) / visible.length : 0);

    const goTo = (idx) => {
      current = Math.max(0, Math.min(visible.length - 1, idx));
      main.scrollTo(
        isVertical()
          ? { top: main.offsetHeight * current, behavior: 'smooth' }
          : { left: main.offsetWidth * current, behavior: 'smooth' }
      );
      syncUI(current);
    };

    // Show only the images belonging to one colourway, plus the ones attached to
    // no colour at all (flat lays, hardware) which belong to all of them.
    //
    // A colour with no photographs of its own would filter down to nothing, so
    // it opts out and shows the whole gallery instead. That is the state the
    // entire catalogue is in until per-colour shots are attached to variants,
    // and it is why this is safe to ship ahead of the photography.
    const showColorway = (cw) => {
      const hasOwn = allSlides.some((s) => s.dataset.colorway === cw);
      allSlides.forEach((s) => {
        const own = s.dataset.colorway || '';
        s.toggleAttribute('hidden', hasOwn && own !== '' && own !== cw);
      });
      recount();
      current = 0;
      // Jump, don't glide: this is a change of subject, not a step through one.
      main.scrollTo(isVertical() ? { top: 0 } : { left: 0 });
      syncUI(0);
    };

    window.FRPdpGallery = { showColorway, goTo };

    // The bar tracks the gesture continuously, so it gets its own undebounced
    // listener rather than waiting on the 100ms settle below.
    if (fill) {
      main.addEventListener('scroll', () => {
        setFill(isVertical()
          ? (main.scrollTop + main.offsetHeight) / main.scrollHeight
          : (main.scrollLeft + main.offsetWidth) / main.scrollWidth);
      }, { passive: true });
    }

    // Crossing the breakpoint leaves the fill scaled on the wrong axis, which
    // reads as a bar stuck empty. Re-assert it against the new orientation.
    window.matchMedia('(min-width: 769px)').addEventListener('change', () => syncUI(current));

    // Reflect position 1 of n at load rather than an empty bar.
    syncUI(0);

    // Track the settled index. The continuous handler above already paints the
    // bar mid-gesture; this is what keeps `current` honest for goTo and for the
    // breakpoint change, and snaps the fill onto an exact 1/n at rest.
    let scrollTimeout;
    main.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const idx = isVertical()
          ? Math.round(main.scrollTop / main.offsetHeight)
          : Math.round(main.scrollLeft / main.offsetWidth);
        if (idx !== current) {
          current = idx;
          syncUI(current);
        }
      }, 100);
    }, { passive: true });
  };

  /* --- PDP colours panel ("View the colors") --- */
  const initPdpColorsPanel = () => {
    const panel = document.querySelector('[data-colors-panel]');
    if (!panel) return;
    const pdp = panel.closest('[data-pdp]');

    const close = () => {
      panel.classList.remove('is-active');
      panel.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    };
    const open = () => {
      panel.classList.add('is-active');
      panel.setAttribute('aria-hidden', 'false');
      // Only the mobile panel takes the screen; on desktop the page behind it
      // is the gallery you are comparing against, so it stays scrollable.
      if (!window.matchMedia('(min-width: 769px)').matches) {
        document.body.style.overflow = 'hidden';
      }
    };

    document.querySelectorAll('[data-colors-open]').forEach((b) =>
      b.addEventListener('click', (e) => { e.preventDefault(); open(); }));
    panel.querySelectorAll('[data-colors-close]').forEach((b) =>
      b.addEventListener('click', close));
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && panel.classList.contains('is-active')) close();
    });

    // Picking a colour here is the same choice as picking a chip. Drive the
    // chip rather than following the href: a page load would throw away the
    // gallery position and the panel for a change we can make in place.
    panel.addEventListener('click', (e) => {
      const pick = e.target.closest('[data-colors-pick]');
      if (!pick || !pdp) return;
      const chip = pdp.querySelector('[data-variant-select] [data-colorway="' + pick.dataset.colorsPick + '"]');
      if (!chip) return; // no matching chip — let the link navigate
      e.preventDefault();
      chip.click();
      panel.querySelectorAll('[data-colors-pick]').forEach((a) => {
        a.classList.toggle('is-current', a === pick);
        if (a === pick) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
      close();
    });
  };

  /* --- Localization selectors (country + language) --- */
  const initLocalization = () => {
    document.querySelectorAll('[data-loc-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const form = select.closest('form');
        if (form) form.submit();
      });
    });
  };

  /* --- Footer accordions (menu columns only) --- */
  const initFooterAccordions = () => {
    document.querySelectorAll('.footer__menu-group .footer__accordion-trigger').forEach((trigger) => {
      trigger.addEventListener('click', () => {
        const content = trigger.nextElementSibling;
        if (!content) return;
        const open = content.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    });
  };

  /* --- Newsletter: expandable fields + Klaviyo profile properties --- */
  /* --- Country picker: a searchable control over a native <select> ---
     Used by the Shipping and Returns tabs, from snippets/country-picker.liquid.

     The select stays the state. Everything else in the delivery and returns
     features reads select.value, writes it, looks up option[value=XX] and
     listens for `change`; this only swaps what the shopper touches, and
     dispatches the same `change` a native pick would. Nothing downstream knows.

     Not shared with initCountryCombo in the footer, which looks similar and is
     not: that one is a free-text field whose value IS the country name, for
     Klaviyo, with no codes and nothing listening. Merging them would mean one
     component serving two different contracts — the trap CLAUDE.md names about
     the two colour->hex ladders that disagreed. */
  const initCountryPicker = () => {
    document.querySelectorAll('[data-country-picker]').forEach((root) => {
      const select = root.querySelector('select');
      const native = root.querySelector('[data-cpick-native]');
      const trigger = root.querySelector('[data-cpick-trigger]');
      const panel = root.querySelector('[data-cpick-panel]');
      const search = root.querySelector('[data-cpick-search]');
      const list = root.querySelector('[data-cpick-list]');
      const empty = root.querySelector('[data-cpick-empty]');
      if (!select || !trigger || !panel || !search || !list) return;

      // One country with nothing to choose between is not a control. Markets
      // unconfigured renders exactly that, and a button opening a list of one
      // is worse than the line of text it replaced.
      const options = [...select.options].map((o) => ({ code: o.value, name: o.textContent.trim() }));
      if (options.length < 2) return;

      // The swap. Only here, after every reason to bail has been checked, so a
      // shopper never loses the native control to a button that cannot work.
      if (native) native.hidden = true;
      trigger.hidden = false;

      let shown = [];
      let active = -1;

      const label = (code) => {
        const hit = options.find((o) => o.code === code);
        return hit ? hit.name : code;
      };
      const syncTrigger = () => {
        const el = root.querySelector('[data-cpick-value]');
        if (el) el.textContent = label(select.value);
      };

      const close = (refocus) => {
        panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
        search.removeAttribute('aria-activedescendant');
        active = -1;
        if (refocus) trigger.focus();
      };

      const highlight = (i) => {
        active = i;
        [...list.children].forEach((li, n) => li.setAttribute('aria-selected', String(n === i)));
        const li = list.children[i];
        if (li) {
          search.setAttribute('aria-activedescendant', li.id);
          li.scrollIntoView({ block: 'nearest' });
        }
      };

      const choose = (code) => {
        if (select.value !== code) {
          select.value = code;
          syncTrigger();
          // bubbles, because the listeners that matter are on the select itself
          // but a delegated one would be missed by a non-bubbling event.
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
        close(true);
      };

      // Starts-with before contains, so typing "ind" offers India before
      // Indonesia rather than by whatever order the market list happens to be
      // in. No cap on the count: the list scrolls, and truncating it silently
      // is how a shopper concludes their country is not served.
      const render = () => {
        const q = search.value.trim().toLowerCase();
        const starts = options.filter((o) => o.name.toLowerCase().startsWith(q));
        const rest = q ? options.filter((o) => !o.name.toLowerCase().startsWith(q) && o.name.toLowerCase().includes(q)) : [];
        shown = starts.concat(rest);
        list.innerHTML = '';
        shown.forEach((o, i) => {
          const li = document.createElement('li');
          li.id = select.id + '-opt-' + i;
          li.className = 'cpick__option';
          li.setAttribute('role', 'option');
          li.setAttribute('aria-selected', String(o.code === select.value));
          if (o.code === select.value) li.classList.add('is-current');
          li.textContent = o.name;
          // mousedown, not click: it lands before the input's blur closes the
          // panel, which is what makes a tap on the list register at all.
          li.addEventListener('mousedown', (e) => { e.preventDefault(); choose(o.code); });
          list.appendChild(li);
        });
        if (empty) empty.hidden = !!shown.length;
        active = -1;
      };

      const open = () => {
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        search.value = '';
        render();
        // The current country, scrolled to rather than merely marked: on a
        // 144-row list the mark is below the fold and the panel opens looking
        // like nothing is selected.
        const at = shown.findIndex((o) => o.code === select.value);
        if (at >= 0 && list.children[at]) list.children[at].scrollIntoView({ block: 'center' });
        search.focus();
      };

      trigger.addEventListener('click', () => (panel.hidden ? open() : close(true)));
      search.addEventListener('input', render);

      search.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); highlight(Math.min(shown.length - 1, active + 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(Math.max(0, active - 1)); }
        else if (e.key === 'Enter') {
          // The first match on Enter, so a shopper who types enough to be
          // unambiguous never has to reach for the list.
          if (shown.length) { e.preventDefault(); choose(shown[active >= 0 ? active : 0].code); }
        } else if (e.key === 'Escape') { e.preventDefault(); close(true); }
      });

      // Closing on an outside pointerdown rather than on the input's blur:
      // blur fires when focus moves into the list itself, and the panel would
      // shut under the finger that was reaching for a country.
      document.addEventListener('pointerdown', (e) => {
        if (!panel.hidden && !root.contains(e.target)) close(false);
      });

      // Another picker, or the footer's market selector, moved the country.
      // The select is already updated by the time this runs; the button is not.
      select.addEventListener('change', syncTrigger);
      document.addEventListener('fr:country', syncTrigger);

      syncTrigger();
    });
  };

  /* --- Country type-ahead (footer newsletter "dream destination") ---
     Types to filter the country list from the hidden [data-country-source]
     select: names that start with the text first, then names that contain it.
     Arrows move, Enter or a tap picks, Escape closes. Anything that is not a
     country on the list fails validation, so the stored value stays clean. */
  const initCountryCombo = () => {
    document.querySelectorAll('[data-country-combo]').forEach((combo) => {
      const input = combo.querySelector('input');
      const list = combo.querySelector('[role="listbox"]');
      const source = combo.querySelector('[data-country-source]');
      if (!input || !list || !source) return;
      const countries = [...new Set([...source.options].map((o) => o.value.trim()).filter((v) => v && v !== '---'))];
      const exact = (v) => countries.find((c) => c.toLowerCase() === v.trim().toLowerCase());
      let shown = [];
      let active = -1;

      const close = () => { list.hidden = true; input.setAttribute('aria-expanded', 'false'); input.removeAttribute('aria-activedescendant'); active = -1; };
      const validate = () => {
        const v = input.value.trim();
        input.setCustomValidity(v && !exact(v) ? 'Please choose a country from the list.' : '');
      };
      const pick = (name) => { input.value = name; validate(); close(); };
      const highlight = (i) => {
        active = i;
        [...list.children].forEach((li, n) => li.setAttribute('aria-selected', String(n === i)));
        const li = list.children[i];
        if (li) { input.setAttribute('aria-activedescendant', li.id); li.scrollIntoView({ block: 'nearest' }); }
      };
      const render = () => {
        const q = input.value.trim().toLowerCase();
        if (!q) { close(); return; }
        const starts = countries.filter((c) => c.toLowerCase().startsWith(q));
        const contains = countries.filter((c) => !c.toLowerCase().startsWith(q) && c.toLowerCase().includes(q));
        shown = starts.concat(contains).slice(0, 8);
        list.innerHTML = '';
        shown.forEach((c, i) => {
          const li = document.createElement('li');
          li.id = input.id + '-opt-' + i;
          li.className = 'footer__nl-combo-option';
          li.setAttribute('role', 'option');
          li.textContent = c;
          // mousedown, not click: it lands before the input's blur closes the list
          li.addEventListener('mousedown', (e) => { e.preventDefault(); pick(c); });
          list.appendChild(li);
        });
        list.hidden = !shown.length;
        input.setAttribute('aria-expanded', String(!!shown.length));
        active = -1;
      };

      input.addEventListener('input', () => { validate(); render(); });
      input.addEventListener('keydown', (e) => {
        if (list.hidden) return;
        if (e.key === 'ArrowDown') { e.preventDefault(); highlight(Math.min(shown.length - 1, active + 1)); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); highlight(Math.max(0, active - 1)); }
        else if (e.key === 'Enter') { if (shown.length) { e.preventDefault(); pick(shown[active >= 0 ? active : 0]); } }
        else if (e.key === 'Escape') close();
      });
      input.addEventListener('blur', () => {
        const match = exact(input.value);
        if (match) input.value = match; // "japan" -> "Japan"
        validate();
        close();
      });
    });
  };

  const initNewsletterForms = () => {
    const sendToKlaviyo = (props) => {
      if (!props || !props['$email']) return;
      try { window.klaviyo = window.klaviyo || []; window.klaviyo.push(['identify', props]); } catch (e) {}
      try { window._learnq = window._learnq || []; window._learnq.push(['identify', props]); } catch (e) {}
    };

    // identify() alone records a profile and subscribes nobody. When the form
    // names a list, the address joins it through Klaviyo's client API — the
    // same call the Foreign World globe makes. Never blocks the Shopify post:
    // it settles on success, failure or after 1.5s, whichever comes first.
    const subscribeToList = (form, email, props) => {
      const company = form.dataset.klaviyoCompany;
      const list = form.dataset.klaviyoList;
      if (!company || !list || !window.fetch) return Promise.resolve();
      const attrs = { email, subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } } };
      if (props['$first_name']) attrs.first_name = props['$first_name'];
      if (props['$last_name']) attrs.last_name = props['$last_name'];
      const extra = {};
      if (props.Title) extra.Title = props.Title;
      if (props.Birthday) extra.Birthday = props.Birthday;
      if (props['Dream destination']) extra['Dream destination'] = props['Dream destination'];
      if (Object.keys(extra).length) attrs.properties = extra;
      const body = { data: {
        type: 'subscription',
        attributes: { custom_source: 'Footer newsletter', profile: { data: { type: 'profile', attributes: attrs } } },
        relationships: { list: { data: { type: 'list', id: list } } }
      } };
      const req = fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(company), {
        method: 'POST',
        keepalive: true,
        headers: { 'content-type': 'application/json', revision: '2025-07-15' },
        body: JSON.stringify(body)
      }).catch(() => {});
      return Promise.race([req, new Promise((r) => setTimeout(r, 1500))]);
    };

    const bind = (scope) => {
      const form = scope.tagName === 'FORM' ? scope : scope.querySelector('form');
      // With a Klaviyo embed there is no <form> in the scope at bind time — Klaviyo
      // injects one later — but the collapsible still has to open. Bind the
      // expander off the scope and only wire submit when a form is actually here.
      if (scope.dataset.nlBound) return;
      scope.dataset.nlBound = '1';
      if (form) form.dataset.nlBound = '1';

      const fields = scope.querySelector('[data-nl-fields]'); // collapsible (footer) or null
      const expanders = scope.querySelectorAll('[data-nl-expand]');
      const reqs = scope.querySelectorAll('[data-nl-required]');
      const trigger = scope.querySelector('[data-nl-expand]');

      const setReq = (on) => reqs.forEach((el) => (on ? el.setAttribute('required', '') : el.removeAttribute('required')));

      // A Klaviyo embed takes over only once Klaviyo has actually drawn a form
      // into it. A form that is not Live draws nothing, and the theme form stays.
      const embed = scope.querySelector('[data-nl-embed]');
      let useEmbed = false;
      let open = false;
      const panel = () => (useEmbed ? embed : fields);
      if (embed && form) {
        const adopt = () => {
          if (useEmbed || !embed.querySelector('form, input')) return;
          useEmbed = true;
          setReq(false);
          if (fields) fields.hidden = true;
          form.hidden = true;
          embed.hidden = !open;
        };
        new MutationObserver(adopt).observe(embed, { childList: true, subtree: true });
        adopt();
      }

      const expand = () => {
        const el = panel(); if (!el) return;
        open = true; el.hidden = false; if (!useEmbed) setReq(true);
        if (trigger) trigger.setAttribute('aria-expanded', 'true');
      };
      const collapse = () => {
        const el = panel(); if (!el) return;
        open = false; el.hidden = true; setReq(false);
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      };

      expanders.forEach((b) => b.addEventListener('click', () => (open ? collapse() : expand())));

      if (!form) return;

      form.addEventListener('submit', (e) => {
        // Clicking Register while collapsed just opens the form.
        if (fields && fields.hidden) { e.preventDefault(); expand(); return; }
        if (form.dataset.nlSent) return;
        const getVal = (sel) => { const el = form.querySelector(sel); return el ? el.value.trim() : ''; };
        const em = getVal('[name="contact[email]"]');
        if (!em) return;
        const props = { '$email': em };
        const fn = getVal('[name="contact[first_name]"]'); if (fn) props['$first_name'] = fn;
        const ln = getVal('[name="contact[last_name]"]'); if (ln) props['$last_name'] = ln;
        const gender = form.querySelector('[name="nl_gender"]:checked'); if (gender && gender.value) props['Title'] = gender.value;
        const bday = getVal('[data-birthday]'); if (bday) props['Birthday'] = bday;
        const dream = getVal('[data-dream-destination]'); if (dream) props['Dream destination'] = dream;
        // Foreign World join = opt-in to be featured on the globe (delineates
        // globe members from plain email subscribers).
        if (form.closest('[data-globe-optin]')) props['globe_opt_in'] = true;
        e.preventDefault();
        form.dataset.nlSent = '1';
        sendToKlaviyo(props);
        subscribeToList(form, em, props).then(() => form.submit());
      });
    };

    document.querySelectorAll('[data-nl-section]').forEach(bind);
    document.querySelectorAll('[data-nl-form]').forEach((el) => {
      if (el.closest('[data-nl-section]')) return;
      const form = el.closest('form');
      if (form) bind(form);
    });
  };

  /* --- PDP Mobile Band ATC button --- */
  const initPdpMobileBand = () => {
    const mobileBtn = document.querySelector('[data-mobile-submit]');
    const form = document.querySelector('[data-add-to-cart]');
    if (!mobileBtn || !form) return;
    mobileBtn.addEventListener('click', () => form.requestSubmit());
  };

  /* --- SmartSize: park the injected size links in the Sizing tab ---
     SIZE GUIDE and FIND MY SIZE come from SmartSize APP EMBEDS, not from the
     section's app block. An embed is injected and positioned by the app's own
     JS, so Liquid cannot place it — moving the {% render block %} slot moved
     the block and left the embeds where they were. They are reparented here
     instead, once they exist, into the Sizing panel where the fit information
     belongs. The section app block already renders into .pdp__size-guide-slot
     inside that panel and needs no move.

     Buttons only. The app also appends its size-chart modal to <body>; that has
     to stay there, because a modal inside a display:none panel cannot open.

     Each node is moved once and stamped. If the app relocates one back we let
     it be rather than trading moves with it for the life of the page. */
  const initSmartSizePlacement = () => {
    const panel = document.querySelector('[data-tab-panel="sizing"]');
    if (!panel) return;

    // The app injects into .pdp__details-inner:
    //   #smartrecom-triggers
    //     #smartrecom-sizechart-injected-button > #smartrecom-sizechart-trigger  SIZE CHART
    //     #smartrecom-inline-button-injected    > #smartrecom-button             FIND MY SIZE
    // Its size-chart modal goes to <body> separately and is left there — a modal
    // inside a display:none panel could not open.
    //
    // Both are restyled by component-product-page.css into one stacked pair:
    // Find my size full width, Size chart a quiet link under it.
    //
    // THE TWO BUTTONS ARRIVE INDEPENDENTLY, and that is the whole difficulty.
    // Moving the wrapper the moment it appears can beat the second button to it,
    // and the app then drops that one back into .pdp__details-inner where it is
    // never seen again — which is why previews showed SIZE CHART alone while the
    // theme editor, with fewer scripts competing, happened to win the race and
    // showed both.
    //
    // So this adopts rather than moves once: the wrapper goes to the panel, and
    // any trigger container that lands outside afterwards is pulled in after it.
    const adopt = () => {
      const wrap = document.getElementById('smartrecom-triggers');
      // Where the tools belong in the panel. An anchor, not the end: the fit
      // line closes the panel and the tools sit above it. Falls back to the
      // panel itself, because the theme can be running a mix of versions and an
      // older pdp-tabs has no anchor in it - appending is wrong by one element,
      // not broken.
      const slot = panel.querySelector('[data-size-tools]') || panel;
      if (wrap && !slot.contains(wrap)) {
        slot.appendChild(wrap);
      }

      const home = slot.querySelector('#smartrecom-triggers') || wrap;
      if (!home || !panel.contains(home)) return;

      // Find my size first, Size chart under it. The app emits them the other
      // way round, so the node moves here - which is what puts the primary
      // first for a keyboard and a screen reader.
      //
      // THIS MOVE IS NOT WHAT THE SHOPPER SEES. The app reveals the wrapper
      // with an inline display:flex and stamps order:0 on the chart and
      // order:1 on this button, so flexbox lays them out in ITS order and
      // ignores the DOM. component-product-page.css restates the order
      // inverted with !important, which is what an important author rule can
      // do to a normal inline style. Both halves are load-bearing: drop this
      // move and a screen reader reads the chart first, drop the CSS and the
      // panel shows SIZE CHART on top however these nodes are arranged.
      //
      // Inside adopt(), so it re-asserts if the app re-renders its triggers.
      const primary = home.querySelector('#smartrecom-inline-button-injected');
      if (primary && home.firstElementChild !== primary) home.prepend(primary);

      // The chart's label, from the section setting. The app writes it in
      // capitals, and CSS cannot reach sentence case from there: text-transform
      // does not compose, so lowercasing the span and capitalising its first
      // letter is the furthest it goes - which gives "Size chart" and no way to
      // capitalise the second word. Writing the text node is the only route to
      // an arbitrary label, and it makes the wording a merchant setting rather
      // than the app's.
      //
      // Re-asserted on every adopt pass, so an app re-render inside the
      // observer's window is corrected. After that window the app's own label
      // would win, which is the same uppercase it ships today - a visible
      // wording change, never a broken control.
      const wanted = (panel.dataset.sizeChartLabel || '').trim();
      if (wanted) {
        const label = home.querySelector('#smartrecom-sizechart-trigger .smartrecom-trigger-text');
        if (label && label.textContent.trim() !== wanted) label.textContent = wanted;
      }

      // --inline is the injected button wrappers specifically; the app's other
      // furniture, the modal included, does not carry it.
      document.querySelectorAll('.smartrecom-container--inline').forEach((c) => {
        if (!panel.contains(c)) home.appendChild(c);
      });
    };

    adopt();
    // Everything lands after this file runs, so watch for it. Each pass leaves
    // the nodes inside the panel, so the observer's own moves are a no-op on the
    // next pass and this cannot loop.
    const mo = new MutationObserver(adopt);
    mo.observe(document.body, { childList: true, subtree: true });
    // Long enough for a storefront carrying a dozen app scripts to get there.
    // The previous 15s was tuned against the theme editor, which is not the
    // slow case.
    setTimeout(() => mo.disconnect(), 45000);
  };

  /* --- Initialize Everything (each isolated so one failure can't break others) --- */
  /* --- Product recommendations: fetch the real ones, or show none --- *\
   *
   * Shopify only populates `recommendations` when the section is requested
   * through the Recommendations API, so the PDP's first render has an empty,
   * hidden band. This fills it.
   *
   * The response is the WHOLE section re-rendered with the object filled, so we
   * lift its grid out and drop it in. Anything other than a grid with items in
   * it leaves the band hidden — a store with nothing to recommend for this
   * product shows no band, which is the honest outcome and the reason the
   * placeholder branch this replaces had to go.
   */
  const initProductRecs = () => {
    const sec = document.querySelector('[data-product-recommendations]');
    if (!sec) return;

    const block = sec.querySelector('[data-recs-block]');
    const grid = sec.querySelector('[data-recs-grid]');
    const productId = sec.dataset.recsProductId;
    const sectionId = sec.dataset.recsSectionId;
    if (!block || !grid || !productId || !sectionId) return;

    // Scroll indicator under the mobile rail: the thumb is as wide as the
    // share of the row on screen and slides with it. Hidden when nothing
    // overflows, which is always the case on the desktop grid.
    const bar = sec.querySelector('[data-recs-progress]');
    const thumb = bar && bar.firstElementChild;
    const updateProgress = () => {
      if (!thumb) return;
      const max = grid.scrollWidth - grid.clientWidth;
      bar.hidden = max <= 1;
      if (max <= 1) return;
      const share = grid.clientWidth / grid.scrollWidth;
      const pos = grid.scrollLeft / max;
      thumb.style.width = (share * 100) + '%';
      thumb.style.transform = 'translateX(' + (pos * (1 / share - 1) * 100) + '%)';
    };
    grid.addEventListener('scroll', updateProgress, { passive: true });
    window.addEventListener('resize', updateProgress);

    // Already filled server-side (the API's own render of this section) — the
    // fetch below would be asking the same question twice.
    if (grid.querySelector('.product-recs__item')) { block.hidden = false; updateProgress(); return; }

    const base = sec.dataset.recsUrl || '/recommendations/products';
    const limit = parseInt(sec.dataset.recsLimit, 10) || 4;
    const url = base + '?section_id=' + encodeURIComponent(sectionId) +
                '&product_id=' + encodeURIComponent(productId) +
                '&limit=' + limit;

    fetch(url)
      .then((r) => (r.ok ? r.text() : Promise.reject(new Error('HTTP ' + r.status))))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-recs-grid]');
        if (!fresh || !fresh.querySelector('.product-recs__item')) return;
        grid.innerHTML = fresh.innerHTML;
        block.hidden = false;
        updateProgress();
        // Images arriving late change the row's width.
        grid.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', updateProgress, { once: true });
        });
        // The cards carry the gallery and wishlist behaviour the rest of the
        // theme binds on load, and these arrived after that.
        try { initProductCardGalleries(); } catch (e) {}
        try { initBadgeContrast(); } catch (e) {}
        try { initWishlist(); } catch (e) {}
      })
      .catch(() => { /* no band rather than a wrong one */ });
  };

  /* --- The Sizing panel says something, or says nothing on purpose --- */
  const initSizingFallback = () => {
    const panel = document.querySelector('[data-tab-panel="sizing"]');
    const note = panel && panel.querySelector('[data-sizing-fallback]');
    if (!panel || !note) return;

    // Anything in the panel that is not the fallback itself counts as content —
    // a size guide button, the model line, or SmartSize's injected triggers.
    const hasContent = () => Array.from(panel.children)
      .some((el) => el !== note && (el.textContent.trim() || el.querySelector('img,svg,button,a,input')));

    const settle = () => { note.hidden = hasContent(); };
    settle();

    // SmartSize injects after load, so an empty panel now may not be empty in a
    // second. Watched rather than timed: a timeout would either fire too early
    // on a slow connection or leave the note flashing on a fast one.
    const obs = new MutationObserver(settle);
    obs.observe(panel, { childList: true, subtree: true });
    setTimeout(() => obs.disconnect(), 10000);
  };

  /* --- Hero video player (snippets/hero-video.liquid) ---
     Each [data-hero-video] host holds one <video> and, when the merchant
     turns controls on, one [data-hv-controls] bar. The video autoplays muted;
     it pauses while off screen and resumes on return — unless the visitor
     paused it, which is respected until they press play again. */
  const initHeroVideos = () => {
    const hosts = document.querySelectorAll('[data-hero-video]');
    if (!hosts.length) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const players = [];

    hosts.forEach((host) => {
      const video = host.querySelector('video');
      if (!video) return;
      video.muted = true; // iOS will not autoplay otherwise, attribute or not

      const bar = host.querySelector('[data-hv-controls]');
      const playBtn = bar && bar.querySelector('[data-hv-play]');
      const soundBtn = bar && bar.querySelector('[data-hv-sound]');
      const progress = bar && bar.querySelector('[data-hv-progress]');
      const fill = bar && bar.querySelector('[data-hv-fill]');
      let userPaused = reduceMotion;
      let inView = true;
      let raf = 0;

      const paint = () => {
        if (!fill || !video.duration) return;
        const ratio = Math.min(1, video.currentTime / video.duration);
        fill.style.transform = `scaleX(${ratio})`;
        const pct = Math.round(ratio * 100);
        progress.setAttribute('aria-valuenow', pct);
        progress.setAttribute('aria-valuetext', `${pct}%`);
      };
      const tick = () => { paint(); raf = requestAnimationFrame(tick); };

      const sync = () => {
        const playing = !video.paused;
        host.classList.toggle('is-playing', playing);
        host.classList.toggle('is-unmuted', !video.muted);
        if (playBtn) playBtn.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
        if (soundBtn) {
          soundBtn.setAttribute('aria-label', video.muted ? 'Turn sound on' : 'Turn sound off');
          soundBtn.setAttribute('aria-pressed', String(!video.muted));
        }
        cancelAnimationFrame(raf);
        if (playing && fill) raf = requestAnimationFrame(tick);
        else paint();
      };
      ['play', 'pause', 'volumechange', 'loadedmetadata', 'seeked'].forEach((ev) => video.addEventListener(ev, sync));

      const play = () => { const p = video.play(); if (p && p.catch) p.catch(() => sync()); };
      if (reduceMotion) video.pause();

      const player = { video, host, get inView() { return inView; }, set inView(v) {
        inView = v;
        if (!v) video.pause();
        else if (!userPaused) play();
      } };
      players.push(player);

      if (bar) {
        bar.hidden = false;

        playBtn.addEventListener('click', () => {
          if (video.paused) { userPaused = false; play(); }
          else { userPaused = true; video.pause(); }
        });

        soundBtn.addEventListener('click', () => {
          const unmute = video.muted;
          // One voice at a time: sound on here turns it off everywhere else
          if (unmute) players.forEach((p) => { if (p.video !== video) p.video.muted = true; });
          video.muted = !unmute;
          if (unmute && video.paused) { userPaused = false; play(); }
        });

        bar.querySelector('[data-hv-skip]').addEventListener('click', () => {
          if (!video.duration) return;
          video.currentTime = (video.currentTime + 10) % video.duration;
        });

        bar.querySelector('[data-hv-fullscreen]').addEventListener('click', () => {
          // Native controls while full screen: ours are laid over the hero, not the video
          if (video.requestFullscreen) {
            video.controls = true;
            video.requestFullscreen().catch(() => { video.controls = false; });
          } else if (video.webkitEnterFullscreen) {
            video.webkitEnterFullscreen(); // iOS Safari: the video element only
          }
        });
        document.addEventListener('fullscreenchange', () => {
          if (document.fullscreenElement !== video) video.controls = false;
        });
        video.addEventListener('webkitendfullscreen', () => { if (!userPaused) play(); });

        // Scrubbing — pointer for mouse and touch, arrows for keyboard
        const seekTo = (clientX) => {
          if (!video.duration) return;
          const r = progress.getBoundingClientRect();
          const ratio = Math.min(1, Math.max(0, (clientX - r.left) / r.width));
          video.currentTime = ratio * video.duration;
          paint();
        };
        let dragging = false;
        progress.addEventListener('pointerdown', (e) => {
          dragging = true;
          progress.setPointerCapture(e.pointerId);
          seekTo(e.clientX);
        });
        progress.addEventListener('pointermove', (e) => { if (dragging) seekTo(e.clientX); });
        const endDrag = () => { dragging = false; };
        progress.addEventListener('pointerup', endDrag);
        progress.addEventListener('pointercancel', endDrag);
        progress.addEventListener('keydown', (e) => {
          if (!video.duration) return;
          const step = { ArrowLeft: -5, ArrowDown: -5, ArrowRight: 5, ArrowUp: 5 }[e.key];
          if (e.key === 'Home') video.currentTime = 0;
          else if (e.key === 'End') video.currentTime = Math.max(0, video.duration - 0.1);
          else if (step) video.currentTime = Math.min(video.duration, Math.max(0, video.currentTime + step));
          else return;
          e.preventDefault();
          paint();
        });
      }

      sync();
    });

    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const p = players.find((pl) => pl.host === entry.target);
        if (p) p.inView = entry.isIntersecting;
      });
    }, { threshold: 0.15 });
    players.forEach((p) => io.observe(p.host));
  };

  /* --- Chat: opened from Contact Us, never floating ---
     base.css keeps <shopify-chat> undisplayed until it carries `open`, the
     component's own declarative switch. A [data-open-chat] link sets it. The
     link keeps its real href, so when Inbox is missing or has not loaded yet
     the click is left alone and the shopper lands on the contact page. */
  const initChatLinks = () => {
    document.addEventListener('click', (e) => {
      const link = e.target.closest('[data-open-chat]');
      if (!link || e.metaKey || e.ctrlKey || e.shiftKey || e.button > 0) return;
      const chat = document.querySelector('shopify-chat');
      if (!chat || !customElements.get('shopify-chat')) return;
      e.preventDefault();
      chat.setAttribute('open', '');
    });
  };

  const init = () => {
    [
      initReveal, initHeroVideos, initMobileMenu, initShopMenu, initChrome, initAnnouncement, initStickyHeader,
      initCarousels, initVariantSelectors, initQuantitySelectors, initAccordions,
      initModals, initCartDrawer, initProductCardGalleries, initBadgeContrast, initFilters, initAddToCart,
      initWishlist, initWishlistDrawer, initSearchDrawer, initPdpTabs, initPdpGallery,
      initPdpMobileBand, initKlaviyoBisPark, initPdpNotify, initPdpVariants, initPdpColorsPanel, initDeliveryEstimator, initContentPanels, initPdpRows, initPdpZoom, initPdpStickyBand, initSmartSizePlacement, initShopPromise, initReturnsByCountry, initCountryPicker, initOverlayContrast,
      initFooterAccordions, initChatLinks, initCountryCombo, initNewsletterForms, initLocalization, initProductRecs, initSizingFallback,
    ].forEach((fn) => {
      try { fn(); } catch (e) { console.error('[FR init]', fn.name, e); }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
